import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  FormControl,
  InputLabel,
  Skeleton,
  Tooltip,
  Avatar,
  Divider,
  Collapse,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PaletteIcon from '@mui/icons-material/Palette';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MovieIcon from '@mui/icons-material/Movie';
import PersonIcon from '@mui/icons-material/Person';
import TitleIcon from '@mui/icons-material/Title';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import GroupsIcon from '@mui/icons-material/Groups';
import SubjectIcon from '@mui/icons-material/Subject';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RefreshIcon from '@mui/icons-material/Refresh';
import TvIcon from '@mui/icons-material/Tv';

import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd', bg: '#f8f9fa', surface: '#ffffff',
  surfaceLow: '#f1f4f5', text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#adb3b5', emerald: '#10b981',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

// ── Sortable helpers (for huisstijl drag & drop) ──────────────────────────
function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    touchAction: 'none',
  };
  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </Box>
  );
}

function SortableArea({ items, getId, onReorderSubset, children }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = React.useMemo(() => items.map(getId), [items, getId]);
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorderSubset(arrayMove(items, oldIdx, newIdx));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

const StitchIcon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const SECTOR_COLORS = {
  'Key account': { color: '#f59e0b', bg: '#fef3c7' },
  'Premium account': { color: '#3b82f6', bg: '#dbeafe' },
  'Normaal account': { color: T.outline, bg: T.surfaceLow },
  'B2B': { color: '#3b82f6', bg: '#dbeafe' },
  'Semi-public': { color: T.tertiary, bg: T.tertiaryContainer },
  'Public': { color: T.emerald, bg: '#ecfdf5' },
  'Agency': { color: '#f59e0b', bg: '#fef3c7' },
};

const TYPE_CONFIG = {
  Logo: { icon: <ImageIcon />, color: '#3b82f6', bg: '#dbeafe', label: "Logo's" },
  Kleur: { icon: <PaletteIcon />, color: '#ec4899', bg: '#fce7f3', label: 'Kleuren' },
  Font: { icon: <FontDownloadIcon />, color: '#10b981', bg: '#d1fae5', label: 'Fonts' },
  Brandguide: { icon: <DescriptionIcon />, color: '#8b5cf6', bg: '#ede9fe', label: 'Brand Guidelines' },
  'After Effects': { icon: <MovieIcon />, color: '#f59e0b', bg: '#fef3c7', label: 'After Effects Templates' },
  Narrowcasting: { icon: <TvIcon />, color: '#7c3aed', bg: '#ede9fe', label: 'Narrowcasting Templates' },
  Overig: { icon: <AttachFileIcon />, color: '#6b7280', bg: '#f3f4f6', label: 'Overige bestanden' },
};

const TYPES = ['Logo', 'Kleur', 'Font', 'Brandguide', 'After Effects', 'Narrowcasting', 'Overig'];
const AE_SUBTYPES = ['Intro', 'Outro', 'Naamtitel', 'Titel'];
const AE_FORMATS = ['16x9', '4x5', '1x1'];
const AE_FIELDS = ['Naam', 'Functie', 'Titel', 'Subtitel', 'URL'];
const KLEUR_SUBTYPES = ['Primair', 'Secundair', 'Accent', 'Tekst', 'Achtergrond'];
const FONT_SUBTYPES = ['Heading', 'Body', 'Accent'];
const FONT_SOURCES = ['Bestand', 'Adobe Fonts', 'Google Fonts', 'Extern'];

const FONT_SOURCE_COLORS = {
  Bestand:        { color: '#4b5563', bg: '#f3f4f6', border: '#d1d5db' },
  'Adobe Fonts':  { color: '#be123c', bg: '#fff1f2', border: '#fecdd3' },
  'Google Fonts': { color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
  Extern:         { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
};

// ============================================================================
// Configuratie Manager (per-compositie config for AE templates)
// ============================================================================
const AE_POSITIE_OPTIONS = ['L', 'R', 'Beide'];

function getDefaultCompName(type, formaat, positie, velden) {
  if (type === 'Naamtitel') {
    const functie = velden?.includes('Functie') ? '_Functie' : '';
    if (positie === 'Beide') {
      return `Naamtitel_Template_L${functie}_${formaat}, Naamtitel_Template_R${functie}_${formaat}`;
    }
    const pos = positie || 'L';
    return `Naamtitel_Template_${pos}${functie}_${formaat}`;
  }
  return `${type}_Template_${formaat}`;
}

function ConfiguratieManager({ elementId, configuraties: initialConfigs, onRefresh }) {
  const [configs, setConfigs] = useState(initialConfigs || []);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state for add/edit
  const [formType, setFormType] = useState('Naamtitel');
  const [formFormaat, setFormFormaat] = useState('16x9');
  const [formVelden, setFormVelden] = useState([]);
  const [formPositie, setFormPositie] = useState('Beide');
  const [formCompNaam, setFormCompNaam] = useState('');
  const [formCompNaamManual, setFormCompNaamManual] = useState(false);
  const [formLabel, setFormLabel] = useState('');

  // Sync from parent when initialConfigs changes
  useEffect(() => { setConfigs(initialConfigs || []); }, [initialConfigs]);

  const getVisibleFields = (type) => {
    if (type === 'Naamtitel') return ['Naam', 'Functie'];
    return ['Titel', 'Subtitel', 'URL'];
  };

  const resetForm = (cfg) => {
    if (cfg) {
      setFormType(cfg.fields?.Type || 'Naamtitel');
      setFormFormaat(cfg.fields?.Formaat || '16x9');
      setFormVelden(cfg.fields?.Velden || []);
      setFormPositie(cfg.fields?.Positie || 'Beide');
      setFormCompNaam(cfg.fields?.Composititenaam || '');
      setFormCompNaamManual(!!cfg.fields?.Composititenaam);
      setFormLabel(cfg.fields?.Label || '');
    } else {
      setFormType('Naamtitel');
      setFormFormaat('16x9');
      setFormVelden(['Naam', 'Functie']);
      setFormPositie('Beide');
      setFormCompNaam('');
      setFormCompNaamManual(false);
      setFormLabel('');
    }
  };

  // Auto-generate comp name when type/formaat/positie/velden change
  useEffect(() => {
    if (!formCompNaamManual) {
      setFormCompNaam(getDefaultCompName(formType, formFormaat, formPositie, formVelden));
    }
  }, [formType, formFormaat, formPositie, formVelden, formCompNaamManual]);

  const handleAddStandard = async (type) => {
    setSaving(true);
    try {
      const veldenMap = { Naamtitel: ['Naam', 'Functie'], Titel: ['Titel', 'Subtitel', 'URL'], Intro: ['Titel', 'Subtitel'], Outro: ['Titel', 'Subtitel'] };
      const velden = veldenMap[type] || ['Titel', 'Subtitel'];
      const positie = type === 'Naamtitel' ? 'Beide' : null;

      for (const formaat of AE_FORMATS) {
        const compositienaam = type === 'Naamtitel' ? null : `${type}_Template_${formaat}`;
        const body = { type, formaat, velden, positie, compositienaam, label: null };
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      toast.success(`${type} configuraties toegevoegd (${AE_FORMATS.length} formaten)`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Types that already have configs (to grey out standard buttons)
  const existingTypes = new Set(configs.map(c => c.fields?.Type).filter(Boolean));

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const body = {
        type: formType,
        formaat: formFormaat,
        velden: formVelden,
        compositienaam: (formType === 'Naamtitel' && formPositie === 'Beide') ? null : formCompNaam,
        positie: formType === 'Naamtitel' ? formPositie : null,
        label: formLabel || null,
      };

      if (editingId) {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${editingId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Configuratie bijgewerkt');
      } else {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Configuratie toegevoegd');
      }
      setAdding(false);
      setEditingId(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!(await bevestig({ titel: 'Configuratie verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${configId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Configuratie verwijderd');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    }
  };

  const handleDeleteGroup = async (typeName) => {
    const groupConfigs = configs.filter(c => c.fields?.Type === typeName);
    if (!(await bevestig({ titel: `Alle ${typeName}-configuraties verwijderen?`, tekst: `${groupConfigs.length} configuratie(s) worden verwijderd. Dit kan niet ongedaan worden gemaakt.`, knop: 'Alles verwijderen', gevaarlijk: true }))) return;
    setSaving(true);
    try {
      for (const cfg of groupConfigs) {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${cfg.id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      toast.success(`${groupConfigs.length} ${typeName} configuraties verwijderd`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (cfg) => {
    resetForm(cfg);
    setEditingId(cfg.id);
    setAdding(true);
  };

  const startAdd = () => {
    resetForm(null);
    setEditingId(null);
    setAdding(true);
  };

  const visibleFields = getVisibleFields(formType);

  // Detect duplicate comp names
  const aeCompNames = configs.map(c => c.fields?.Composititenaam).filter(Boolean);
  const aeDuplicateComps = new Set(aeCompNames.filter((name, i) => aeCompNames.indexOf(name) !== i));

  const renderEditForm = () => (
    <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2, bgcolor: '#fafafa' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}>
        {editingId ? 'Configuratie bewerken' : 'Nieuwe configuratie'}
      </Typography>

      {/* Type */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Type</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {AE_SUBTYPES.map(t => (
            <Chip key={t} label={t} size="small"
              onClick={() => {
                setFormType(t);
                setFormVelden(t === 'Naamtitel' ? ['Naam', 'Functie'] : ['Titel']);
                setFormCompNaamManual(false);
              }}
              variant={formType === t ? 'filled' : 'outlined'}
              color={formType === t ? 'warning' : 'default'}
              sx={{ fontWeight: formType === t ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      </Box>

      {/* Formaat */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Formaat</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {AE_FORMATS.map(f => (
            <Chip key={f} label={f} size="small"
              onClick={() => { setFormFormaat(f); setFormCompNaamManual(false); }}
              variant={formFormaat === f ? 'filled' : 'outlined'}
              color={formFormaat === f ? 'warning' : 'default'}
              sx={{ fontWeight: formFormaat === f ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      </Box>

      {/* Velden */}
      <Box sx={{ mb: formType === 'Naamtitel' ? 1.5 : 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Velden</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {visibleFields.map(f => {
            const selected = formVelden.includes(f);
            return (
              <Chip key={f} label={f} size="small"
                onClick={() => setFormVelden(prev => selected ? prev.filter(v => v !== f) : [...prev, f])}
                variant={selected ? 'filled' : 'outlined'}
                color={selected ? 'warning' : 'default'}
                sx={{ fontWeight: selected ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Positie (only for Naamtitel) */}
      {formType === 'Naamtitel' && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Positie</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {AE_POSITIE_OPTIONS.map(p => (
              <Chip key={p} label={p} size="small"
                onClick={() => { setFormPositie(p); setFormCompNaamManual(false); }}
                variant={formPositie === p ? 'filled' : 'outlined'}
                color={formPositie === p ? 'warning' : 'default'}
                sx={{ fontWeight: formPositie === p ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Compositienaam (hidden for Naamtitel+Beide since both L and R comps exist) */}
      {!(formType === 'Naamtitel' && formPositie === 'Beide') && (
        <TextField
          label="Compositienaam"
          value={formCompNaam}
          onChange={(e) => { setFormCompNaam(e.target.value); setFormCompNaamManual(true); }}
          size="small"
          fullWidth
          sx={{ mb: 1.5 }}
          helperText={formCompNaamManual ? 'Handmatig ingesteld' : 'Auto-gegenereerd'}
          InputProps={{
            sx: { fontSize: '0.85rem' },
          }}
        />
      )}

      {/* Label */}
      <TextField
        label="Label (optioneel)"
        value={formLabel}
        onChange={(e) => setFormLabel(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        placeholder="Bijv. 'Met logo links'"
        InputProps={{ sx: { fontSize: '0.85rem' } }}
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={handleSaveConfig} disabled={saving || !formType || !formFormaat} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
          {saving ? 'Opslaan...' : (editingId ? 'Bijwerken' : 'Toevoegen')}
        </Button>
        <Button size="small" onClick={() => { setAdding(false); setEditingId(null); }} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
          Annuleren
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
        Configuraties ({configs.length})
      </Typography>

      {/* Existing configuraties grouped by type */}
      {configs.length > 0 && (() => {
        const grouped = {};
        configs.forEach(cfg => {
          const t = cfg.fields?.Type || 'Overig';
          if (!grouped[t]) grouped[t] = [];
          grouped[t].push(cfg);
        });
        const typeOrder = ['Naamtitel', 'Titel', 'Intro', 'Outro'];
        const sortedTypes = Object.keys(grouped).sort((a, b) => {
          const ia = typeOrder.indexOf(a), ib = typeOrder.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        });

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
            {sortedTypes.map(typeName => (
              <Box key={typeName}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#92400e' }}>
                    {typeName}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDeleteGroup(typeName)} disabled={saving} sx={{ p: 0.25, color: '#ef4444', opacity: 0.5, '&:hover': { opacity: 1 } }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {grouped[typeName].map(cfg => (
                    editingId === cfg.id ? (
                      <React.Fragment key={cfg.id}>{renderEditForm()}</React.Fragment>
                    ) : (
                    <Box
                      key={cfg.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        p: 0.75,
                        pl: 1.5,
                        borderRadius: 1,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fafafa',
                        '&:hover': { borderColor: '#f59e0b' },
                      }}
                    >
                      <Chip label={cfg.fields?.Formaat || '?'} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500, fontSize: '0.7rem' }} />
                      {cfg.fields?.Positie && cfg.fields.Type === 'Naamtitel' && (
                        <Chip label={cfg.fields.Positie} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(cfg.fields?.Velden || []).map(v => (
                          <Chip key={v} label={v} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                        ))}
                      </Box>
                      {cfg.fields?.Label && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', flexShrink: 0 }} noWrap>
                          {cfg.fields.Label}
                        </Typography>
                      )}
                      {aeDuplicateComps.has(cfg.fields?.Composititenaam) && (
                        <Chip label="⚠ Dubbele comp" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 600 }} />
                      )}
                      <IconButton size="small" onClick={() => startEdit(cfg)} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteConfig(cfg.id)} sx={{ p: 0.25, color: '#ef4444' }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    )
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Add new form (only when adding, not editing) */}
      {adding && !editingId ? (
        renderEditForm()
      ) : !adding && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Standard config quick-add */}
          {AE_SUBTYPES.filter(t => !existingTypes.has(t)).length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
                Standaard toevoegen (alle formaten)
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {AE_SUBTYPES.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    onClick={() => !saving && !existingTypes.has(t) && handleAddStandard(t)}
                    disabled={saving || existingTypes.has(t)}
                    variant="outlined"
                    sx={{ cursor: existingTypes.has(t) ? 'default' : 'pointer', fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </Box>
          )}
          <Button size="small" startIcon={<AddIcon />} onClick={startAdd} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
            Handmatig toevoegen
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Narrowcasting Configuratie Manager
// ============================================================================
const NC_FOTO_LAYERS = ['Foto_1', 'Foto_2', 'Foto_3', 'Foto_4'];
const NC_TITEL_LAYERS = ['Titel_1', 'Titel_2', 'Titel_3', 'Titel_4'];
const NC_SUBTITEL_LAYERS = ['Subtitel_1', 'Subtitel_2', 'Subtitel_3', 'Subtitel_4'];

function getNcCompName(slideType, slideNum, formaat) {
  if (slideType === 'begin') return `Slide_Begin_${formaat}`;
  if (slideType === 'eind') return `Slide_Eind_${formaat}`;
  return `Slide_${slideNum}_${formaat}`;
}

function getNcSlideLabel(slideType, slideNum) {
  if (slideType === 'begin') return 'Begin slide';
  if (slideType === 'eind') return 'Eind slide';
  return `Content slide ${slideNum}`;
}

function parseNcSlideType(cfgType) {
  if (cfgType === 'Slide_Begin') return { slideType: 'begin', slideNum: 0 };
  if (cfgType === 'Slide_Eind') return { slideType: 'eind', slideNum: 0 };
  const m = cfgType?.match(/^Slide_(\d+)$/);
  if (m) return { slideType: 'content', slideNum: parseInt(m[1], 10) };
  // Legacy Slide_Content format
  if (cfgType === 'Slide_Content') return { slideType: 'content', slideNum: 1 };
  return { slideType: 'content', slideNum: 1 };
}

function encodeNcSlideType(slideType, slideNum) {
  if (slideType === 'begin') return 'Slide_Begin';
  if (slideType === 'eind') return 'Slide_Eind';
  return `Slide_${slideNum}`;
}

function NarrowcastingConfiguratieManager({ elementId, configuraties: initialConfigs, onRefresh }) {
  const [configs, setConfigs] = useState(initialConfigs || []);
  const [editing, setEditing] = useState(null); // { id, slideType, slideNum, velden, compNaam, compManual, label }
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  // Format — shared across all slides, derived from first config or default
  const [formaat, setFormaat] = useState(() => {
    const first = (initialConfigs || [])[0];
    return first?.fields?.Formaat || '16x9';
  });

  useEffect(() => { setConfigs(initialConfigs || []); }, [initialConfigs]);

  // Filter slide configs (exclude Background and Muziek)
  const slideConfigs = configs.filter(c => {
    const t = c.fields?.Type || '';
    return t !== 'Background' && t !== 'Muziek';
  });

  // Sort slide configs: begin first, then content by number, then eind
  const sortedConfigs = [...slideConfigs].sort((a, b) => {
    const pa = parseNcSlideType(a.fields?.Type);
    const pb = parseNcSlideType(b.fields?.Type);
    const order = { begin: 0, content: 1, eind: 2 };
    if (order[pa.slideType] !== order[pb.slideType]) return order[pa.slideType] - order[pb.slideType];
    return pa.slideNum - pb.slideNum;
  });

  const resetEdit = (cfg) => {
    if (cfg) {
      const parsed = parseNcSlideType(cfg.fields?.Type);
      setEditing({
        id: cfg.id,
        slideType: parsed.slideType,
        slideNum: parsed.slideNum || 1,
        velden: cfg.fields?.Velden || [],
        compNaam: cfg.fields?.Composititenaam || '',
        compManual: !!cfg.fields?.Composititenaam,
        label: cfg.fields?.Label || '',
      });
    } else {
      // New content slide — find next number
      const contentNums = configs
        .map(c => parseNcSlideType(c.fields?.Type))
        .filter(p => p.slideType === 'content')
        .map(p => p.slideNum);
      const nextNum = contentNums.length > 0 ? Math.max(...contentNums) + 1 : 1;
      setEditing({
        id: null,
        slideType: 'content',
        slideNum: nextNum,
        velden: ['Titel_1', 'Subtitel_1', 'Foto_1'],
        compNaam: '',
        compManual: false,
        label: '',
      });
    }
  };

  const updateEditing = (updates) => setEditing(prev => ({ ...prev, ...updates }));

  // Auto comp name
  const editCompName = editing
    ? (editing.compManual ? editing.compNaam : getNcCompName(editing.slideType, editing.slideNum, formaat))
    : '';

  const handleFormaatChange = async (newFormaat) => {
    setFormaat(newFormaat);
    // Update all existing configs' formaat + comp names
    if (configs.length === 0) return;
    setSaving(true);
    try {
      for (const cfg of configs) {
        const parsed = parseNcSlideType(cfg.fields?.Type);
        const newComp = getNcCompName(parsed.slideType, parsed.slideNum, newFormaat);
        await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${cfg.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formaat: newFormaat, compositienaam: newComp }),
        });
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Formaat bijwerken mislukt: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const type = encodeNcSlideType(editing.slideType, editing.slideNum);
      const comp = editing.compManual ? editing.compNaam : getNcCompName(editing.slideType, editing.slideNum, formaat);
      const body = {
        type,
        formaat,
        velden: editing.velden,
        compositienaam: comp,
        label: editing.label || null,
      };

      if (editing.id) {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Configuratie bijgewerkt');
      } else {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Configuratie toegevoegd');
      }
      setEditing(null);
      setAdding(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!(await bevestig({ titel: 'Configuratie verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${configId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Configuratie verwijderd');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    }
  };

  const handleDuplicate = async (cfg) => {
    setSaving(true);
    try {
      const parsed = parseNcSlideType(cfg.fields?.Type);
      // Find next content number
      const contentNums = configs
        .map(c => parseNcSlideType(c.fields?.Type))
        .filter(p => p.slideType === 'content')
        .map(p => p.slideNum);
      const nextNum = parsed.slideType === 'content'
        ? (contentNums.length > 0 ? Math.max(...contentNums) + 1 : parsed.slideNum + 1)
        : parsed.slideNum;
      const newType = parsed.slideType === 'content'
        ? encodeNcSlideType('content', nextNum)
        : cfg.fields?.Type;
      const newComp = parsed.slideType === 'content'
        ? getNcCompName('content', nextNum, formaat)
        : cfg.fields?.Composititenaam + '_copy';

      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: newType,
          formaat,
          velden: cfg.fields?.Velden || [],
          compositienaam: newComp,
          label: cfg.fields?.Label || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Slide gedupliceerd als ${getNcSlideLabel(parsed.slideType === 'content' ? 'content' : parsed.slideType, nextNum)}`);
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddStandard = async () => {
    setSaving(true);
    try {
      const slides = [
        { type: 'Slide_Begin', velden: ['Titel_1', 'Subtitel_1', 'Foto_1'] },
        { type: 'Slide_1', velden: ['Titel_1', 'Subtitel_1', 'Foto_1', 'Foto_2'] },
        { type: 'Slide_Eind', velden: ['Titel_1', 'Subtitel_1', 'Foto_1'] },
      ];
      for (const s of slides) {
        await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: s.type, formaat, velden: s.velden,
            compositienaam: getNcCompName(
              s.type === 'Slide_Begin' ? 'begin' : s.type === 'Slide_Eind' ? 'eind' : 'content',
              1, formaat
            ),
          }),
        });
      }
      toast.success('Standaard slides toegevoegd');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Edit form ──
  const renderEditForm = () => {
    if (!editing) return null;
    return (
      <Box sx={{ border: '1px solid #ddd6fe', borderRadius: 1, p: 2, bgcolor: '#faf5ff' }}>
        {/* Slide type */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Slide type</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {['begin', 'content', 'eind'].map(t => (
              <Chip key={t} label={t === 'content' ? 'Content' : t === 'begin' ? 'Begin' : 'Eind'} size="small"
                onClick={() => updateEditing({ slideType: t, compManual: false })}
                variant={editing.slideType === t ? 'filled' : 'outlined'}
                color={editing.slideType === t ? 'secondary' : 'default'}
                sx={{ fontWeight: editing.slideType === t ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
              />
            ))}
            {editing.slideType === 'content' && (
              <TextField
                label="Nr"
                type="number"
                value={editing.slideNum}
                onChange={(e) => updateEditing({ slideNum: Math.max(1, parseInt(e.target.value) || 1), compManual: false })}
                size="small"
                sx={{ width: 70, ml: 0.5 }}
                InputProps={{ sx: { fontSize: '0.85rem' }, inputProps: { min: 1 } }}
              />
            )}
          </Box>
        </Box>

        {/* Titel layers */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Titel layers</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {NC_TITEL_LAYERS.map(v => {
              const sel = editing.velden.includes(v);
              return <Chip key={v} label={v} size="small" onClick={() => updateEditing({ velden: sel ? editing.velden.filter(x => x !== v) : [...editing.velden, v] })}
                variant={sel ? 'filled' : 'outlined'} color={sel ? 'secondary' : 'default'}
                sx={{ fontWeight: sel ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }} />;
            })}
          </Box>
        </Box>

        {/* Subtitel layers */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Subtitel layers</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {NC_SUBTITEL_LAYERS.map(v => {
              const sel = editing.velden.includes(v);
              return <Chip key={v} label={v} size="small" onClick={() => updateEditing({ velden: sel ? editing.velden.filter(x => x !== v) : [...editing.velden, v] })}
                variant={sel ? 'filled' : 'outlined'} color={sel ? 'secondary' : 'default'}
                sx={{ fontWeight: sel ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }} />;
            })}
          </Box>
        </Box>

        {/* Foto layers */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Foto layers</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {NC_FOTO_LAYERS.map(v => {
              const sel = editing.velden.includes(v);
              return <Chip key={v} label={v} size="small" onClick={() => updateEditing({ velden: sel ? editing.velden.filter(x => x !== v) : [...editing.velden, v] })}
                variant={sel ? 'filled' : 'outlined'} color={sel ? 'secondary' : 'default'}
                sx={{ fontWeight: sel ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }} />;
            })}
          </Box>
        </Box>

        {/* Comp naam */}
        <TextField label="Compositienaam" value={editCompName}
          onChange={(e) => updateEditing({ compNaam: e.target.value, compManual: true })}
          size="small" fullWidth sx={{ mb: 1.5 }}
          helperText={editing.compManual ? 'Handmatig' : 'Auto-gegenereerd'}
          InputProps={{ sx: { fontSize: '0.85rem' } }} />

        {/* Label */}
        <TextField label="Label (optioneel)" value={editing.label}
          onChange={(e) => updateEditing({ label: e.target.value })}
          size="small" fullWidth sx={{ mb: 2 }} placeholder="Bijv. 'Slide met 2 foto's'"
          InputProps={{ sx: { fontSize: '0.85rem' } }} />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={handleSave} disabled={saving}
            sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
            {saving ? 'Opslaan...' : (editing.id ? 'Bijwerken' : 'Toevoegen')}
          </Button>
          <Button size="small" onClick={() => { setEditing(null); setAdding(false); }} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
            Annuleren
          </Button>
        </Box>
      </Box>
    );
  };

  // ── Backgrounds ──
  const bgConfigs = configs.filter(c => c.fields?.Type === 'Background');
  const [bgEditing, setBgEditing] = useState(null); // { id, label, comp } or { id: null } for new
  const [muziekUploading, setMuziekUploading] = useState(false);

  const handleSaveBg = async () => {
    if (!bgEditing || !bgEditing.comp?.trim()) return;
    setSaving(true);
    try {
      if (bgEditing.id) {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${bgEditing.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ compositienaam: bgEditing.comp.trim(), label: bgEditing.label?.trim() || null }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Background bijgewerkt');
      } else {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'Background', formaat, compositienaam: bgEditing.comp.trim(), label: bgEditing.label?.trim() || null }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Background toegevoegd');
      }
      setBgEditing(null);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  // ── Muziek ──
  const muziekConfigs = configs.filter(c => c.fields?.Type === 'Muziek');
  const [muziekEditing, setMuziekEditing] = useState(null); // { id, label } or { id: null } for new

  const handleSaveMuziek = async () => {
    if (!muziekEditing || !muziekEditing.label?.trim()) return;
    setSaving(true);
    try {
      if (muziekEditing.id) {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${muziekEditing.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: muziekEditing.label.trim() }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Muziek bijgewerkt');
      } else {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/configuraties`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'Muziek', formaat, label: muziekEditing.label.trim() }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        toast.success('Muziek aangemaakt');
      }
      setMuziekEditing(null);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleUploadMuziekFile = async (configId, fileType, file) => {
    setMuziekUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/configuratie/${configId}/upload-muziek`, {
        method: 'POST', body: formData,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`${fileType === 'nummer' ? 'Nummer' : 'Outro'} geüpload`);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.message); }
    finally { setMuziekUploading(false); }
  };

  // Detect duplicate comp names across all configs
  const allCompNames = configs
    .map(c => c.fields?.Composititenaam)
    .filter(Boolean);
  const duplicateComps = new Set(allCompNames.filter((name, i) => allCompNames.indexOf(name) !== i));

  return (
    <Box>
      {/* ═══ SLIDES ═══ */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
        Slide configuraties ({slideConfigs.length})
      </Typography>

      {/* Formaat selector */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>Formaat</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {AE_FORMATS.map(f => (
            <Chip key={f} label={f} size="small"
              onClick={() => handleFormaatChange(f)}
              variant={formaat === f ? 'filled' : 'outlined'}
              color={formaat === f ? 'secondary' : 'default'}
              disabled={saving}
              sx={{ fontWeight: formaat === f ? 600 : 400, cursor: 'pointer', fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      </Box>

      {/* Slide list */}
      {sortedConfigs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          {sortedConfigs.map(cfg => {
            const parsed = parseNcSlideType(cfg.fields?.Type);
            if (editing?.id === cfg.id) return <React.Fragment key={cfg.id}>{renderEditForm()}</React.Fragment>;
            return (
              <Box key={cfg.id} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75, pl: 1.5,
                borderRadius: 1, border: '1px solid #e5e7eb', bgcolor: '#fafafa',
                '&:hover': { borderColor: '#7c3aed' },
              }}>
                <TvIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#5b21b6', minWidth: 90 }}>
                  {getNcSlideLabel(parsed.slideType, parsed.slideNum)}
                </Typography>
                <Typography variant="caption" sx={{
                  fontSize: '0.65rem', fontFamily: 'monospace',
                  color: duplicateComps.has(cfg.fields?.Composititenaam) ? '#dc2626' : 'text.secondary',
                  fontWeight: duplicateComps.has(cfg.fields?.Composititenaam) ? 700 : 400,
                }} noWrap>
                  {cfg.fields?.Composititenaam || ''}{duplicateComps.has(cfg.fields?.Composititenaam) ? ' ⚠' : ''}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(cfg.fields?.Velden || []).map(v => (
                    <Chip key={v} label={v} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                  ))}
                </Box>
                <IconButton size="small" onClick={() => resetEdit(cfg)} sx={{ p: 0.25 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                <Tooltip title="Dupliceren"><IconButton size="small" onClick={() => handleDuplicate(cfg)} disabled={saving} sx={{ p: 0.25, color: '#7c3aed' }}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                <IconButton size="small" onClick={() => handleDelete(cfg.id)} sx={{ p: 0.25, color: '#ef4444' }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {adding && !editing?.id ? renderEditForm() : !adding && !editing && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {slideConfigs.length === 0 && (
            <Button size="small" variant="outlined" onClick={handleAddStandard} disabled={saving}
              sx={{ textTransform: 'none', borderColor: '#7c3aed', color: '#7c3aed' }}>
              Standaard slides
            </Button>
          )}
          <Button size="small" startIcon={<AddIcon />} onClick={() => { resetEdit(null); setAdding(true); }}
            sx={{ textTransform: 'none', color: '#7c3aed' }}>
            Slide toevoegen
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ═══ BACKGROUNDS ═══ */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
        Backgrounds ({bgConfigs.length})
      </Typography>

      {bgConfigs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
          {bgConfigs.map(cfg => (
            bgEditing?.id === cfg.id ? (
              <Box key={cfg.id} sx={{ border: '1px solid #fde68a', borderRadius: 1, p: 2, bgcolor: '#fffbeb' }}>
                <TextField label="Naam" size="small" fullWidth value={bgEditing.label}
                  onChange={(e) => setBgEditing(prev => ({ ...prev, label: e.target.value }))} sx={{ mb: 1 }}
                  InputProps={{ sx: { fontSize: '0.85rem' } }} />
                <TextField label="Compositienaam" size="small" fullWidth value={bgEditing.comp}
                  onChange={(e) => setBgEditing(prev => ({ ...prev, comp: e.target.value }))} sx={{ mb: 1.5 }}
                  InputProps={{ sx: { fontSize: '0.85rem' } }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={handleSaveBg} disabled={saving || !bgEditing.comp?.trim()}
                    sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                    Bijwerken
                  </Button>
                  <Button size="small" onClick={() => setBgEditing(null)} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Annuleren</Button>
                </Box>
              </Box>
            ) : (
              <Box key={cfg.id} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75, pl: 1.5,
                borderRadius: 1, border: '1px solid #e5e7eb', bgcolor: '#fafafa',
                '&:hover': { borderColor: '#f59e0b' },
              }}>
                <WallpaperIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#92400e' }}>
                  {cfg.fields?.Label || cfg.fields?.Composititenaam || 'Background'}
                </Typography>
                <Typography variant="caption" sx={{
                  fontSize: '0.65rem', fontFamily: 'monospace', flex: 1,
                  color: duplicateComps.has(cfg.fields?.Composititenaam) ? '#dc2626' : 'text.secondary',
                  fontWeight: duplicateComps.has(cfg.fields?.Composititenaam) ? 700 : 400,
                }} noWrap>
                  {cfg.fields?.Composititenaam || ''}{duplicateComps.has(cfg.fields?.Composititenaam) ? ' ⚠' : ''}
                </Typography>
                <IconButton size="small" onClick={() => setBgEditing({ id: cfg.id, label: cfg.fields?.Label || '', comp: cfg.fields?.Composititenaam || '' })} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(cfg.id)} sx={{ p: 0.25, color: '#ef4444' }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )
          ))}
        </Box>
      )}

      {bgEditing && !bgEditing.id ? (
        <Box sx={{ border: '1px solid #fde68a', borderRadius: 1, p: 2, bgcolor: '#fffbeb', mb: 2 }}>
          <TextField label="Naam" size="small" fullWidth value={bgEditing.label}
            onChange={(e) => setBgEditing(prev => ({ ...prev, label: e.target.value }))} sx={{ mb: 1 }}
            InputProps={{ sx: { fontSize: '0.85rem' } }} />
          <TextField label="Compositienaam (in AE template)" size="small" fullWidth value={bgEditing.comp}
            onChange={(e) => setBgEditing(prev => ({ ...prev, comp: e.target.value }))} sx={{ mb: 1.5 }}
            InputProps={{ sx: { fontSize: '0.85rem' } }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleSaveBg} disabled={saving || !bgEditing.comp?.trim()}
              sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
              Toevoegen
            </Button>
            <Button size="small" onClick={() => setBgEditing(null)} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Annuleren</Button>
          </Box>
        </Box>
      ) : !bgEditing && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setBgEditing({ id: null, label: '', comp: 'Background' })}
          sx={{ textTransform: 'none', color: '#f59e0b', mb: 2 }}>
          Background toevoegen
        </Button>
      )}

      <Divider sx={{ my: 2 }} />

      {/* ═══ MUZIEK ═══ */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
        Muziek ({muziekConfigs.length})
      </Typography>

      {muziekConfigs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
          {muziekConfigs.map(cfg => {
            const velden = cfg.fields?.Velden || [];
            const nummerFile = velden.find(v => v.startsWith?.('nummer_file:'))?.slice(12);
            const outroFile = velden.find(v => v.startsWith?.('outro_file:'))?.slice(11);
            const isEditingThis = muziekEditing?.id === cfg.id;

            return (
              <Box key={cfg.id} sx={{
                p: 1.5, borderRadius: 1, border: '1px solid',
                borderColor: isEditingThis ? '#fbcfe8' : '#e5e7eb',
                bgcolor: isEditingThis ? '#fdf2f8' : '#fafafa',
                '&:hover': { borderColor: '#ec4899' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <MusicNoteIcon sx={{ fontSize: 14, color: '#ec4899' }} />
                  {isEditingThis ? (
                    <TextField size="small" value={muziekEditing.label}
                      onChange={(e) => setMuziekEditing(prev => ({ ...prev, label: e.target.value }))}
                      sx={{ flex: 1 }} InputProps={{ sx: { fontSize: '0.8rem' } }} />
                  ) : (
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', color: '#9d174d', flex: 1 }}>
                      {cfg.fields?.Label || 'Muziek'}
                    </Typography>
                  )}
                  {isEditingThis ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="contained" onClick={handleSaveMuziek} disabled={saving}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' }, minWidth: 0, px: 1 }}>
                        Opslaan
                      </Button>
                      <Button size="small" onClick={() => setMuziekEditing(null)} sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, px: 1 }}>
                        Annuleren
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <IconButton size="small" onClick={() => setMuziekEditing({ id: cfg.id, label: cfg.fields?.Label || '' })} sx={{ p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(cfg.id)} sx={{ p: 0.25, color: '#ef4444' }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', minWidth: 40 }}>Nummer</Typography>
                    {nummerFile && <Chip label={nummerFile} size="small" sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#fce7f3', color: '#9d174d' }} />}
                    <Button size="small" component="label" disabled={muziekUploading}
                      sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#ec4899', p: '2px 6px', minWidth: 0 }}>
                      {nummerFile ? 'Vervang' : 'Upload'}
                      <input type="file" accept="audio/*" hidden onChange={(e) => { if (e.target.files[0]) handleUploadMuziekFile(cfg.id, 'nummer', e.target.files[0]); }} />
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', minWidth: 40 }}>Outro</Typography>
                    {outroFile && <Chip label={outroFile} size="small" sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#fce7f3', color: '#9d174d' }} />}
                    <Button size="small" component="label" disabled={muziekUploading}
                      sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#ec4899', p: '2px 6px', minWidth: 0 }}>
                      {outroFile ? 'Vervang' : 'Upload'}
                      <input type="file" accept="audio/*" hidden onChange={(e) => { if (e.target.files[0]) handleUploadMuziekFile(cfg.id, 'outro', e.target.files[0]); }} />
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {muziekEditing && !muziekEditing.id ? (
        <Box sx={{ border: '1px solid #fbcfe8', borderRadius: 1, p: 2, bgcolor: '#fdf2f8' }}>
          <TextField label="Naam (bijv. 'Corporate upbeat')" size="small" fullWidth value={muziekEditing.label}
            onChange={(e) => setMuziekEditing(prev => ({ ...prev, label: e.target.value }))} sx={{ mb: 1.5 }}
            InputProps={{ sx: { fontSize: '0.85rem' } }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" onClick={handleSaveMuziek} disabled={saving || !muziekEditing.label?.trim()}
              sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' } }}>
              Aanmaken
            </Button>
            <Button size="small" onClick={() => setMuziekEditing(null)} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>Annuleren</Button>
          </Box>
        </Box>
      ) : !muziekEditing && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setMuziekEditing({ id: null, label: '' })}
          sx={{ textTransform: 'none', color: '#ec4899' }}>
          Muziek toevoegen
        </Button>
      )}
    </Box>
  );
}

// ============================================================================
// Account Info
// ============================================================================
function InfoItem({ icon, label, value }) {
  if (!value || !value.trim()) return null;

  // Parse **bold** markdown into <strong> elements
  const formatValue = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: '0.06em', color: T.outline, mb: 0.25 }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: T.text, whiteSpace: 'pre-line' }}>
          {formatValue(value)}
        </Typography>
      </Box>
    </Box>
  );
}

function AccountInfo({ fields }) {
  const sector = fields?.Sector;
  const accountmanager = fields?.Accountmanager;
  const projectleiders = fields?.Projectleiders;
  const projectmanagement = fields?.Projectmanagment;
  const preferredCrew = fields?.['Preffered crew'];

  const hasInfo = accountmanager || projectleiders || (projectmanagement && projectmanagement.trim()) || (preferredCrew && preferredCrew.trim());
  if (!hasInfo) return null;

  return (
    <Box
      sx={{ p: 2.5, mb: 3, borderRadius: '0.375rem', bgcolor: T.surface, boxShadow: T.shadow }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
        <InfoItem
          icon={<BadgeIcon sx={{ fontSize: 18, color: '#6b7280', mt: 0.3 }} />}
          label="Accountmanager"
          value={accountmanager}
        />
        <InfoItem
          icon={<SupervisorAccountIcon sx={{ fontSize: 18, color: '#6b7280', mt: 0.3 }} />}
          label="Projectleiders"
          value={projectleiders}
        />
        <InfoItem
          icon={<AssignmentIcon sx={{ fontSize: 18, color: '#6b7280', mt: 0.3 }} />}
          label="Projectmanagement"
          value={projectmanagement}
        />
        <InfoItem
          icon={<GroupsIcon sx={{ fontSize: 18, color: '#6b7280', mt: 0.3 }} />}
          label="Preferred crew"
          value={preferredCrew}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// Add/Edit Dialog
// ============================================================================
// Bestand van een huisstijl-element downloaden via de backend-proxy
// (geauthenticeerd; werkt ook als de blob-container privé is).
async function downloadElementBestand(elementId, filename) {
  try {
    const r = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${elementId}/bestand`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'bestand';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(`Download mislukt: ${e.message}`);
  }
}

function HuisstijlDialog({ open, onClose, onSave, onDelete, element, defaultType, defaultTypeKleur, defaultTypeFont, defaultBronFont, clientFonts, onRefresh }) {
  const [naam, setNaam] = useState('');
  const [type, setType] = useState('Logo');
  const [typeKleur, setTypeKleur] = useState('');
  const [merk, setMerk] = useState('');
  const [typeFont, setTypeFont] = useState('');
  const [bronFont, setBronFont] = useState('');
  const [bron, setBron] = useState('');
  const [kleurcode, setKleurcode] = useState('');
  const [notitie, setNotitie] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkedFonts, setLinkedFonts] = useState([]);

  // Preview state
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const [previewJobId, setPreviewJobId] = useState(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [workerOnline, setWorkerOnline] = useState(null);
  const [onlineWorkers, setOnlineWorkers] = useState([]);
  const [previewUploading, setPreviewUploading] = useState(false);
  const [previewLightbox, setPreviewLightbox] = useState(false);
  const [previewFrame, setPreviewFrame] = useState(50);
  const [previewConfigId, setPreviewConfigId] = useState('');
  const previewFileRef = useRef(null);

  useEffect(() => {
    if (element) {
      setNaam(element.fields?.Naam || '');
      setType(element.fields?.Type || 'Logo');
      setTypeKleur(element.fields?.['Type (Kleur)'] || '');
      setMerk(element.fields?.['Merk'] || '');
      setTypeFont(element.fields?.['Type (Font)'] || '');
      setBronFont(element.fields?.['Bron (Font)'] || '');
      setBron(element.fields?.Bron || '');
      setKleurcode((element.fields?.Kleurcode || '').toUpperCase());
      setNotitie(element.fields?.Notitie || '');
      setLinkedFonts(element.fields?.['Gekoppelde fonts'] || []);
    } else {
      setNaam('');
      setType(defaultType || 'Logo');
      setTypeKleur(defaultTypeKleur || '');
      setMerk('');
      setTypeFont(defaultTypeFont || '');
      setBronFont(defaultBronFont || '');
      setBron('');
      setKleurcode('');
      setNotitie('');
      setLinkedFonts([]);
    }
    setFile(null);
    setPreviewGenerating(false);
    setPreviewJobId(null);
    setPreviewStatus(null);
    setPreviewUploading(false);
    setPreviewLightbox(false);
  }, [element, defaultType, defaultTypeKleur, defaultTypeFont, defaultBronFont, open]);

  // Helper: Narrowcasting is functionally an AE-type (template, configuraties, fonts)
  const isAeType = type === 'After Effects' || type === 'Narrowcasting';

  // Check worker status when dialog opens for AE element
  useEffect(() => {
    if (!open || !element || (element.fields?.Type !== 'After Effects' && element.fields?.Type !== 'Narrowcasting')) return;
    fetch(`${BACKEND_URL}/Api/name-title/worker-status`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) { setWorkerOnline(data.online); setOnlineWorkers(data.workers || []); } })
      .catch(() => { setWorkerOnline(false); setOnlineWorkers([]); });
  }, [open, element]);

  const checkWorkerStatus = async () => {
    setWorkerOnline(null);
    setOnlineWorkers([]);
    try {
      const [res] = await Promise.all([
        fetch(`${BACKEND_URL}/Api/name-title/worker-status`),
        new Promise(r => setTimeout(r, 2000))
      ]);
      const data = await res.json();
      setWorkerOnline(data.online);
      setOnlineWorkers(data.workers || []);
    } catch {
      setWorkerOnline(false);
      setOnlineWorkers([]);
    }
  };

  // Poll preview job status
  useEffect(() => {
    if (!previewJobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/name-title/status/${previewJobId}`);
        if (!res.ok) return;
        const data = await res.json();
        setPreviewStatus(data);
        if (data.status === 'completed') {
          setPreviewGenerating(false);
          setPreviewJobId(null);
          toast.success('Preview gegenereerd!');
          if (onRefresh) onRefresh();
        } else if (data.status === 'error') {
          setPreviewGenerating(false);
          setPreviewJobId(null);
          toast.error(data.error || 'Preview genereren mislukt');
        }
      } catch (err) {
        // ignore poll errors
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [previewJobId, onRefresh]);

  const handleGeneratePreview = async () => {
    if (!element?.id) return;
    setPreviewGenerating(true);
    setPreviewStatus(null);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/name-title/generate-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ huisstijlElementId: element.id, frame: previewFrame, ...(previewConfigId ? { configuratieId: previewConfigId } : {}) }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPreviewJobId(data.jobId);
      toast.success('Preview job aangemaakt');
    } catch (err) {
      setPreviewGenerating(false);
      toast.error(`Preview starten mislukt: ${err.message}`);
    }
  };

  const handleUploadPreview = async (imageFile) => {
    if (!element?.id || !imageFile) return;
    setPreviewUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${element.id}/upload-preview`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Preview geüpload');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(`Preview upload mislukt: ${err.message}`);
    } finally {
      setPreviewUploading(false);
    }
  };

  const handleFileSelect = (f) => {
    setFile(f);
    if (f && !naam.trim()) {
      setNaam(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSave = async () => {
    if (!naam.trim() || !type) return;
    setSaving(true);
    try {
      await onSave({
        naam: naam.trim(), type,
        typeKleur: type === 'Kleur' ? typeKleur : undefined,
        merk,
        typeFont: type === 'Font' ? typeFont : undefined,
        bronFont: type === 'Font' ? bronFont : undefined,
        bron: (type === 'Font' && bronFont && bronFont !== 'Bestand') ? bron : undefined,
        kleurcode, notitie,
        linkedFonts: isAeType ? linkedFonts : undefined,
      }, file);
      onClose();
    } catch (err) {
      toast.error(`Fout: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const sectionLabel = (text) => (
    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: T.outline, mb: 0.75, display: 'block' }}>
      {text}
    </Typography>
  );

  const pillButton = (label, active, onClick, accent) => (
    <Box
      onClick={onClick}
      sx={{
        px: 1.5, py: 0.5, borderRadius: '9999px', cursor: 'pointer',
        fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: active ? 700 : 500,
        transition: 'all 0.12s',
        ...(active
          ? { bgcolor: accent?.bg || T.primaryContainer, color: accent?.color || T.primary }
          : { bgcolor: T.surfaceLow, color: T.secondary, '&:hover': { bgcolor: '#e8ebee' } }
        ),
      }}
    >
      {label}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.5rem', boxShadow: '0 24px 48px rgba(45,51,53,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '0.375rem', bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StitchIcon name={element ? 'edit' : 'add_circle'} size={20} sx={{ color: T.primary }} />
          </Box>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.15rem', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>
            {element ? 'Element bewerken' : 'Nieuw element'}
          </Typography>
        </Box>
        <Box onClick={onClose} sx={{ width: 32, height: 32, borderRadius: '0.375rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
          <StitchIcon name="close" size={18} sx={{ color: T.outline }} />
        </Box>
      </Box>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 3, pt: 2.5, pb: 1 }}>

        {/* Naam + Merk row */}
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            fullWidth required size="small" placeholder="Naam *"
            sx={{
              flex: 2,
              '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.84rem', '& fieldset': { borderColor: 'rgba(45,51,53,0.1)' } },
            }}
          />
          <TextField
            value={merk}
            onChange={(e) => setMerk(e.target.value)}
            size="small" placeholder="Merk (optioneel)"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.84rem', '& fieldset': { borderColor: 'rgba(45,51,53,0.1)' } },
            }}
          />
        </Box>

        {/* Type selector */}
        <Box>
          {sectionLabel('Type')}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {TYPES.map(t => {
              const config = TYPE_CONFIG[t] || {};
              return pillButton(t, type === t, () => {
                setType(t);
                if (t !== 'Kleur') setTypeKleur('');
                if (t !== 'Font') { setTypeFont(''); setBronFont(''); setBron(''); }
              }, type === t ? { bg: config.bg, color: config.color } : null);
            })}
          </Box>
        </Box>

        {/* ── Kleur specifiek ── */}
        {type === 'Kleur' && (
          <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.375rem', p: 2 }}>
            <Box sx={{ mb: 2 }}>
              {sectionLabel('Kleurtype')}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {KLEUR_SUBTYPES.map(t => pillButton(t, typeKleur === t, () => setTypeKleur(t), { bg: '#fce7f3', color: '#ec4899' }))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                value={kleurcode}
                onChange={(e) => setKleurcode(e.target.value.toUpperCase())}
                size="small" placeholder="#FF5500"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.84rem', bgcolor: T.surface, '& fieldset': { borderColor: 'rgba(45,51,53,0.1)' } },
                }}
              />
              <Box sx={{ position: 'relative', width: 36, height: 36, borderRadius: '0.375rem', overflow: 'hidden', border: '2px solid rgba(45,51,53,0.08)', flexShrink: 0 }}>
                <Box sx={{ width: '100%', height: '100%', bgcolor: kleurcode || '#000000' }} />
                <input
                  type="color"
                  value={kleurcode || '#000000'}
                  onChange={(e) => setKleurcode(e.target.value.toUpperCase())}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Font specifiek ── */}
        {type === 'Font' && (
          <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.375rem', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              {sectionLabel('Fonttype')}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {FONT_SUBTYPES.map(t => pillButton(t, typeFont === t, () => setTypeFont(t), { bg: '#d1fae5', color: '#10b981' }))}
              </Box>
            </Box>
            <Box>
              {sectionLabel('Bron')}
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {FONT_SOURCES.map(t => pillButton(t, bronFont === t, () => { setBronFont(t); if (t === 'Bestand') setBron(''); }, { bg: '#d1fae5', color: '#10b981' }))}
              </Box>
            </Box>
            {bronFont && bronFont !== 'Bestand' && (
              <TextField
                value={bron}
                onChange={(e) => setBron(e.target.value)}
                fullWidth size="small" placeholder="https://fonts.google.com/specimen/..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.84rem', bgcolor: T.surface, '& fieldset': { borderColor: 'rgba(45,51,53,0.1)' } } }}
              />
            )}
          </Box>
        )}

        {/* ── AE / Narrowcasting specifiek ── */}
        {isAeType && (
          <Box sx={{ bgcolor: T.surfaceLow, borderRadius: '0.375rem', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Configuraties */}
            {type === 'After Effects' && element && (
              <ConfiguratieManager elementId={element.id} configuraties={element.configuraties || []} onRefresh={onRefresh} />
            )}
            {type === 'Narrowcasting' && element && (
              <NarrowcastingConfiguratieManager elementId={element.id} configuraties={element.configuraties || []} onRefresh={onRefresh} />
            )}
            {!element && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline, fontStyle: 'italic' }}>
                Sla het element eerst op. Daarna kun je configuraties toevoegen.
              </Typography>
            )}

            {/* Gekoppelde fonts */}
            {clientFonts && clientFonts.length > 0 && (() => {
              const bestandFonts = clientFonts.filter(f => f.fields?.['Bron (Font)'] === 'Bestand');
              if (bestandFonts.length === 0) return null;
              return (
                <Box>
                  {sectionLabel('Gekoppelde fonts')}
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant, mb: 1 }}>
                    Fonts met bron "Bestand" — automatisch geinstalleerd bij render.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {bestandFonts.map(font => {
                      const fontId = font.id;
                      const selected = linkedFonts.includes(fontId);
                      return pillButton(font.fields?.Naam || 'Font', selected, () => {
                        setLinkedFonts(prev => selected ? prev.filter(id => id !== fontId) : [...prev, fontId]);
                      }, { bg: '#fef3c7', color: '#f59e0b' });
                    })}
                  </Box>
                </Box>
              );
            })()}

            {/* Template bestand */}
            <Box>
              {sectionLabel('Template bestand')}
              <Box
                component="label"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1,
                  px: 2, py: 1, borderRadius: '0.375rem', cursor: 'pointer',
                  bgcolor: T.surface, boxShadow: T.shadow,
                  fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.primary,
                  transition: 'all 0.12s', '&:hover': { boxShadow: '0 4px 12px rgba(45,51,53,0.08)' },
                }}
              >
                <StitchIcon name="upload_file" size={16} sx={{ color: T.primary }} />
                {file ? file.name : 'Bestand kiezen'}
                <input type="file" hidden accept=".aep,.zip,.mov" onChange={(e) => handleFileSelect(e.target.files[0] || null)} />
              </Box>
              {(() => {
                const hasBestand = Array.isArray(element?.fields?.Bestand) && element.fields.Bestand.length > 0;
                const currentFilename = hasBestand ? element.fields.Bestand[0]?.filename : null;
                return currentFilename && !file ? (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant, mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    Huidig: {currentFilename}
                    <Box component="span" onClick={() => downloadElementBestand(element.id, currentFilename)}
                      title="Download het huidige templatebestand (bijv. om te bewerken in After Effects)"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', color: T.primary, fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                      <StitchIcon name="download" size={13} sx={{ color: T.primary }} /> downloaden
                    </Box>
                  </Typography>
                ) : null;
              })()}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant, mt: 0.25 }}>
                .aep, .zip (Collect Files) of .mov (pre-rendered)
              </Typography>
            </Box>

            {/* Preview — AE only */}
            {type === 'After Effects' && element && (() => {
              const existingPreview = element?.fields?.Preview;
              const existingPreviewUrl = Array.isArray(existingPreview) && existingPreview.length > 0
                ? (existingPreview[0].thumbnails?.large?.url || existingPreview[0].url)
                : null;
              const hasBestand = Array.isArray(element?.fields?.Bestand) && element.fields.Bestand.length > 0;
              const isMov = hasBestand && element.fields.Bestand[0]?.filename?.toLowerCase().endsWith('.mov');

              return (
                <Box>
                  {sectionLabel('Preview')}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {/* Thumbnail */}
                    <Box
                      onClick={existingPreviewUrl ? () => setPreviewLightbox(true) : undefined}
                      sx={{
                        width: 160, height: 90, borderRadius: '0.375rem', overflow: 'hidden', flexShrink: 0,
                        bgcolor: T.surface, border: '1px solid rgba(45,51,53,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: existingPreviewUrl ? 'zoom-in' : 'default',
                        transition: 'all 0.12s',
                        '&:hover': existingPreviewUrl ? { borderColor: `${T.primary}40`, boxShadow: '0 4px 12px rgba(45,51,53,0.08)' } : {},
                      }}
                    >
                      {existingPreviewUrl ? (
                        <Box component="img" src={existingPreviewUrl} alt="Preview" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <StitchIcon name="image" size={28} sx={{ color: T.outlineVariant }} />
                      )}
                    </Box>

                    {/* Controls */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Worker status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={{
                          width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                          bgcolor: workerOnline === null ? T.outlineVariant : workerOnline ? T.emerald : '#ef4444',
                        }} />
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: workerOnline ? '#065f46' : workerOnline === false ? '#991b1b' : T.outline }}>
                          {workerOnline === null ? 'Checken...' : workerOnline ? `Server actief (${onlineWorkers.length})` : 'Server offline'}
                        </Typography>
                        <Box onClick={checkWorkerStatus} sx={{ cursor: 'pointer', display: 'flex', '&:hover': { opacity: 0.7 } }}>
                          <StitchIcon name="refresh" size={14} sx={{ color: T.outlineVariant }} />
                        </Box>
                      </Box>

                      {/* Config selector */}
                      {(element?.configuraties || []).length > 0 && (
                        <TextField
                          select size="small" value={previewConfigId}
                          onChange={(e) => setPreviewConfigId(e.target.value)}
                          SelectProps={{ native: true }}
                          sx={{
                            width: '100%', mb: 1,
                            '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.75rem', bgcolor: T.surface, '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' } },
                            '& .MuiInputBase-input': { py: '6px' },
                          }}
                        >
                          <option value="">Selecteer configuratie</option>
                          {(element.configuraties || []).map(cfg => (
                            <option key={cfg.id} value={cfg.id}>
                              {cfg.fields?.Type || '?'} — {cfg.fields?.Formaat || '?'}{cfg.fields?.Positie ? ` (${cfg.fields.Positie})` : ''}{cfg.fields?.Label ? ` — ${cfg.fields.Label}` : ''}
                            </option>
                          ))}
                        </TextField>
                      )}

                      {/* Generate + frame + upload row */}
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title={
                          !hasBestand ? 'Upload eerst een template bestand' :
                          isMov ? 'MOV bestanden hebben geen render nodig' :
                          workerOnline === false ? 'Render server is offline' :
                          (!previewConfigId && (element?.configuraties || []).length > 0) ? 'Selecteer eerst een configuratie' :
                          `Render frame ${previewFrame} als preview`
                        }>
                          <span>
                            <Box
                              onClick={(!hasBestand || isMov || workerOnline === false || previewGenerating || (!previewConfigId && (element?.configuraties || []).length > 0)) ? undefined : handleGeneratePreview}
                              sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                                px: 1.5, py: 0.6, borderRadius: '0.375rem', cursor: 'pointer',
                                fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600,
                                bgcolor: T.primaryContainer, color: T.primary,
                                transition: 'all 0.12s',
                                ...(((!hasBestand || isMov || workerOnline === false || previewGenerating || (!previewConfigId && (element?.configuraties || []).length > 0))) && { opacity: 0.4, cursor: 'not-allowed' }),
                                '&:hover': { boxShadow: '0 2px 8px rgba(45,51,53,0.08)' },
                              }}
                            >
                              {previewGenerating ? <CircularProgress size={12} sx={{ color: T.primary }} /> : <StitchIcon name="photo_camera" size={14} sx={{ color: T.primary }} />}
                              {previewGenerating ? 'Bezig...' : 'Genereer'}
                            </Box>
                          </span>
                        </Tooltip>
                        <TextField
                          type="number" size="small" value={previewFrame}
                          onChange={(e) => setPreviewFrame(Math.max(0, parseInt(e.target.value) || 0))}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{
                            width: 56,
                            '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', bgcolor: T.surface, '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' } },
                            '& .MuiInputBase-input': { fontFamily: T.fontBody, fontSize: '0.72rem', py: '4px', px: '6px', textAlign: 'center' },
                          }}
                        />
                        <Box
                          onClick={previewUploading ? undefined : () => previewFileRef.current?.click()}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75,
                            px: 1.5, py: 0.6, borderRadius: '0.375rem', cursor: 'pointer',
                            fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600,
                            bgcolor: T.surface, color: T.secondary, boxShadow: T.shadow,
                            transition: 'all 0.12s',
                            '&:hover': { boxShadow: '0 2px 8px rgba(45,51,53,0.08)' },
                          }}
                        >
                          {previewUploading ? <CircularProgress size={12} /> : <StitchIcon name="upload" size={14} sx={{ color: T.secondary }} />}
                          {previewUploading ? 'Uploaden...' : 'Upload'}
                        </Box>
                        <input ref={previewFileRef} type="file" hidden accept="image/*" onChange={(e) => { const f = e.target.files[0]; if (f) handleUploadPreview(f); e.target.value = ''; }} />
                      </Box>

                      {/* Progress */}
                      {previewGenerating && previewStatus && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <LinearProgress variant="determinate" value={previewStatus.progress || 0} sx={{ flex: 1, height: 3, borderRadius: 1, bgcolor: 'rgba(45,51,53,0.06)', '& .MuiLinearProgress-bar': { bgcolor: T.primary } }} />
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline, minWidth: 28 }}>
                              {previewStatus.progress || 0}%
                            </Typography>
                          </Box>
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant }}>
                            {previewStatus.message}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Lightbox */}
                  {existingPreviewUrl && (
                    <Dialog open={previewLightbox} onClose={() => setPreviewLightbox(false)} maxWidth={false}
                      PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' }, onClick: () => setPreviewLightbox(false) }}>
                      <Box component="img" src={existingPreviewUrl} alt="Preview" sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '0.375rem', cursor: 'pointer' }} />
                    </Dialog>
                  )}
                </Box>
              );
            })()}
          </Box>
        )}

        {/* ── Bestand voor overige types ── */}
        {type !== 'Kleur' && !isAeType && (() => {
          const currentFile = element?.fields?.Bestand?.[0];
          const isImage = currentFile?.type?.startsWith('image/') || /\.(png|jpe?g|gif|svg|webp|bmp|ico|tiff?)$/i.test(currentFile?.filename || '');
          const imageUrl = isImage ? (currentFile?.thumbnails?.large?.url || currentFile?.url) : null;
          const fullImageUrl = isImage ? (currentFile?.thumbnails?.full?.url || currentFile?.url) : null;

          return (
            <Box>
              {sectionLabel('Bestand')}
              {imageUrl && !file && (
                <>
                  <Box
                    onClick={() => setPreviewLightbox(true)}
                    sx={{
                      width: 180, height: 120, borderRadius: '0.375rem', overflow: 'hidden',
                      border: '1px solid rgba(45,51,53,0.06)', cursor: 'zoom-in', mb: 1.25,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: T.surfaceLow, p: 0.5,
                      transition: 'all 0.12s',
                      '&:hover': { borderColor: `${T.primary}40`, boxShadow: '0 4px 12px rgba(45,51,53,0.08)' },
                    }}
                  >
                    <Box component="img" src={imageUrl} alt={element?.fields?.Naam || 'Bestand'} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Dialog open={previewLightbox} onClose={() => setPreviewLightbox(false)} maxWidth={false}
                    PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' }, onClick: () => setPreviewLightbox(false) }}>
                    <Box component="img" src={fullImageUrl} alt={element?.fields?.Naam || 'Bestand'} sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '0.375rem', cursor: 'pointer' }} />
                  </Dialog>
                </>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="label"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1, borderRadius: '0.375rem', cursor: 'pointer',
                    bgcolor: T.surfaceLow, fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.primary,
                    transition: 'all 0.12s', '&:hover': { bgcolor: '#e8ebee' },
                  }}
                >
                  <StitchIcon name="upload_file" size={16} sx={{ color: T.primary }} />
                  {file ? file.name : 'Bestand kiezen'}
                  <input type="file" hidden onChange={(e) => handleFileSelect(e.target.files[0] || null)} />
                </Box>
                {currentFile && !file && (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
                    {currentFile.filename}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })()}

        {/* ── Notitie ── */}
        <TextField
          value={notitie}
          onChange={(e) => setNotitie(e.target.value)}
          fullWidth size="small" multiline minRows={2} maxRows={6}
          placeholder="Notitie (optioneel)"
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '0.375rem', fontFamily: T.fontBody, fontSize: '0.84rem', '& fieldset': { borderColor: 'rgba(45,51,53,0.1)' } },
          }}
        />

        {/* Meta info */}
        {element && <MetaInfo fields={element.fields} showAll />}
      </DialogContent>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderTop: '1px solid rgba(45,51,53,0.06)' }}>
        {element && onDelete && (
          <Box
            onClick={() => { onClose(); onDelete(element); }}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5, mr: 'auto',
              px: 1.5, py: 0.75, borderRadius: '0.375rem', cursor: 'pointer',
              fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: '#ef4444',
              transition: 'all 0.12s', '&:hover': { bgcolor: '#fef2f2' },
            }}
          >
            <StitchIcon name="delete" size={16} sx={{ color: '#ef4444' }} />
            Verwijderen
          </Box>
        )}
        {!(element && onDelete) && <Box sx={{ flex: 1 }} />}
        <Box
          onClick={onClose}
          sx={{
            px: 2.5, py: 1, borderRadius: '0.375rem', cursor: 'pointer',
            fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.secondary,
            transition: 'all 0.12s', '&:hover': { bgcolor: T.surfaceLow },
          }}
        >
          Annuleren
        </Box>
        <Box
          onClick={(!naam.trim() || !type || saving) ? undefined : handleSave}
          sx={{
            px: 2.5, py: 1, borderRadius: '0.375rem', cursor: 'pointer',
            fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: '#fff',
            bgcolor: T.primary, transition: 'all 0.12s',
            ...(((!naam.trim() || !type || saving)) && { opacity: 0.4, cursor: 'not-allowed' }),
            '&:hover': { boxShadow: '0 4px 12px rgba(82,96,117,0.25)' },
          }}
        >
          {saving ? 'Opslaan...' : (element ? 'Bijwerken' : 'Toevoegen')}
        </Box>
      </Box>
    </Dialog>
  );
}

// Helper: format email to display name (rick@fullframe.nl → Rick)
function formatUserEmail(email) {
  if (!email) return null;
  const name = email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Helper: format ISO date to readable string (2026-02-09T12:00:00.000Z → 9 feb 2026)
function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Shared meta info display (toegevoegd/aangepast door + op)
function MetaInfo({ fields, showAll = false }) {
  const addedBy = fields?.['Toegevoegd door'];
  const addedOn = fields?.['Toegevoegd op'];
  const modifiedBy = fields?.['Laatst aangepast door'];
  const modifiedOn = fields?.['Laatst aangepast op'];
  const sx = { color: '#9ca3af', fontSize: '0.65rem' };

  if (showAll) {
    if (!addedBy && !modifiedBy && !addedOn && !modifiedOn) return null;
    return (
      <Box sx={{ mt: 0.5 }}>
        {(addedBy || addedOn) && (
          <Typography variant="caption" sx={sx} display="block">
            Toegevoegd{addedBy && ` door ${formatUserEmail(addedBy)}`}{addedOn && ` op ${formatDate(addedOn)}`}
          </Typography>
        )}
        {(modifiedBy || modifiedOn) && (
          <Typography variant="caption" sx={sx} display="block">
            Aangepast{modifiedBy && ` door ${formatUserEmail(modifiedBy)}`}{modifiedOn && ` op ${formatDate(modifiedOn)}`}
          </Typography>
        )}
      </Box>
    );
  }

  // Compact: only show last modified
  if (!modifiedBy && !modifiedOn) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" sx={sx} display="block">
        Aangepast{modifiedBy && ` door ${formatUserEmail(modifiedBy)}`}{modifiedOn && ` op ${formatDate(modifiedOn)}`}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Color Swatch
// ============================================================================
function ColorSwatch({ element, onEdit, onDelete }) {
  const kleurcode = element.fields?.Kleurcode || '#000000';
  const naam = element.fields?.Naam || '';

  const copyColor = () => {
    navigator.clipboard.writeText(kleurcode);
    toast.success(`${kleurcode} gekopieerd`);
  };

  return (
    <Paper
      elevation={0}
      onClick={() => onEdit(element)}
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer',
        '&:hover': { borderColor: '#ec4899', bgcolor: '#fdf2f8' },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1,
          bgcolor: kleurcode,
          border: '1px solid #e5e7eb',
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{naam}</Typography>
        <Typography
          variant="body2"
          sx={{ color: '#6b7280', fontFamily: 'monospace' }}
        >
          {kleurcode}
        </Typography>
        {element.fields?.Notitie && (
          <Typography variant="caption" color="text.secondary">{element.fields.Notitie}</Typography>
        )}
        <MetaInfo fields={element.fields} />
      </Box>
      <Tooltip title="Kopieer kleurcode">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); copyColor(); }}><ContentCopyIcon fontSize="small" /></IconButton>
      </Tooltip>
      <Tooltip title="Verwijderen">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(element); }} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Paper>
  );
}

// ============================================================================
// File Card (logos, fonts, brand guides, overig)
// ============================================================================
function FileCard({ element, onEdit, onDelete, aeTemplates }) {
  const naam = element.fields?.Naam || '';
  const type = element.fields?.Type || '';
  const bestand = element.fields?.Bestand;
  const file = Array.isArray(bestand) && bestand.length > 0 ? bestand[0] : null;
  const isImage = file?.type?.startsWith('image/') || /\.(png|jpe?g|gif|svg|webp|bmp|ico|tiff?)$/i.test(file?.filename || '');
  const thumbnailUrl = file?.thumbnails?.large?.url || file?.thumbnails?.small?.url || (isImage ? file?.url : null);
  const hasThumbnail = !!thumbnailUrl;
  const fullUrl = isImage ? (file?.thumbnails?.full?.url || file?.url) : null;
  const isFont = type === 'Font' && file?.url && /\.(otf|ttf|woff2?)$/i.test(file?.filename || '');
  const fontFamily = isFont ? `preview-${element.id}` : null;
  const [fontLoaded, setFontLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.Overig;

  useEffect(() => {
    if (!isFont || !fontFamily) return;
    // Fetch font via authenticated backend proxy, then load as FontFace
    authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${element.id}/bestand`)
      .then(r => r.ok ? r.arrayBuffer() : Promise.reject())
      .then(buffer => {
        const face = new FontFace(fontFamily, buffer);
        return face.load();
      })
      .then(loaded => {
        document.fonts.add(loaded);
        setFontLoaded(true);
      })
      .catch(() => {});
  }, [isFont, fontFamily, element.id]);
  // Derive AE sub-type from configuraties
  const cfgs = element.configuraties || [];
  const configTypes = cfgs.map(c => c.fields?.Type).filter(Boolean);
  const firstAeSubType = configTypes[0] || null;
  const fontSubType = element.fields?.['Type (Font)'];
  const bronFont = element.fields?.['Bron (Font)'];
  const bronUrl = element.fields?.Bron;
  const displayIcon = ((type === 'After Effects' || type === 'Narrowcasting') && firstAeSubType && AE_SUBTYPE_ICONS[firstAeSubType])
    ? AE_SUBTYPE_ICONS[firstAeSubType]
    : (type === 'Font' && fontSubType && FONT_SUBTYPE_ICONS[fontSubType])
    ? FONT_SUBTYPE_ICONS[fontSubType]
    : config.icon;

  return (
    <Paper
      elevation={0}
      onClick={() => onEdit(element)}
      sx={{
        borderRadius: 1,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { borderColor: config.color, bgcolor: config.bg },
      }}
    >
      {/* Preview area */}
      {hasThumbnail ? (
        <Box
          onClick={(e) => { e.stopPropagation(); if (isImage) setLightboxOpen(true); }}
          sx={{
            height: 140,
            bgcolor: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            cursor: isImage ? 'zoom-in' : 'pointer',
            '&:hover': { bgcolor: '#f3f4f6' },
          }}
        >
          <img
            src={thumbnailUrl}
            alt={naam}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
      ) : isFont ? (
        <Box
          sx={{
            height: 80,
            bgcolor: '#f0fdf4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              fontFamily: fontLoaded ? `"${fontFamily}", sans-serif` : 'sans-serif',
              fontSize: '1.5rem',
              color: '#166534',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              opacity: fontLoaded ? 1 : 0.3,
              transition: 'opacity 0.3s',
            }}
          >
            {naam}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 80,
            bgcolor: config.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(displayIcon, { sx: { fontSize: 32, color: config.color } })}
        </Box>
      )}

      {/* Info */}
      <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{naam}</Typography>
          {file?.filename && (
            <Chip
              label={file.filename.split('.').pop().toUpperCase()}
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f3f4f6', color: '#6b7280', letterSpacing: '0.02em' }}
            />
          )}
          {element.fields?.['Variatie (Font)'] && (
            <Chip
              label={element.fields['Variatie (Font)']}
              size="small"
              sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.02em',
                bgcolor: element.fields['Variatie (Font)'] === 'Variabel' ? '#ede9fe' : '#f0fdf4',
                color: element.fields['Variatie (Font)'] === 'Variabel' ? '#6d28d9' : '#166534',
              }}
            />
          )}
        </Box>
        {type === 'Font' && bronFont && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Chip
              label={bronFont}
              size="small"
              component={(bronUrl || file) ? 'a' : 'span'}
              href={bronUrl || file?.url || undefined}
              target={(bronUrl || file) ? '_blank' : undefined}
              rel={(bronUrl || file) ? 'noopener noreferrer' : undefined}
              clickable={!!(bronUrl || file)}
              onClick={(e) => e.stopPropagation()}
              icon={(bronUrl || file) ? (bronFont === 'Bestand' ? <DownloadIcon sx={{ fontSize: '14px !important' }} /> : <LinkIcon sx={{ fontSize: '14px !important' }} />) : undefined}
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: FONT_SOURCE_COLORS[bronFont]?.color || '#4b5563',
                bgcolor: FONT_SOURCE_COLORS[bronFont]?.bg || '#f3f4f6',
                border: `1px solid ${FONT_SOURCE_COLORS[bronFont]?.border || '#d1d5db'}`,
                '& .MuiChip-icon': { color: FONT_SOURCE_COLORS[bronFont]?.color || '#4b5563' },
                textDecoration: 'none',
              }}
            />
          </Box>
        )}
        {type === 'Font' && aeTemplates && (() => {
          const linkedIds = element.fields?.['Font gekoppeld aan'] || [];
          if (linkedIds.length === 0) return null;
          const linkedNames = linkedIds.map(id => {
            const tmpl = aeTemplates.find(t => t.id === id);
            return tmpl?.fields?.Naam || null;
          }).filter(Boolean);
          if (linkedNames.length === 0) return null;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
              {linkedNames.map(name => (
                <Chip
                  key={name}
                  icon={<MovieIcon sx={{ fontSize: '12px !important' }} />}
                  label={name}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500, bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#f59e0b' } }}
                />
              ))}
            </Box>
          );
        })()}
        {element.fields?.Notitie && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {element.fields.Notitie}
          </Typography>
        )}
        <MetaInfo fields={element.fields} />
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', mt: 'auto', pt: 1 }}>
          {file && (
            <Tooltip title={`Download ${file.filename || 'bestand'}`}>
              {/* Via de backend-proxy (geauthenticeerd) — een directe blob-URL
                  werkt niet betrouwbaar en de container hoort privé te kunnen. */}
              <IconButton size="small"
                onClick={(e) => { e.stopPropagation(); downloadElementBestand(element.id, file.filename); }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!file && bronUrl && (
            <Tooltip title="Open link">
              <IconButton size="small" component="a" href={bronUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Verwijderen">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(element); }} sx={{ color: '#ef4444' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Lightbox */}
      {fullUrl && (
        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' },
            onClick: () => setLightboxOpen(false),
          }}
        >
          <Box
            component="img"
            src={fullUrl}
            alt={naam}
            sx={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 1,
              cursor: 'pointer',
            }}
          />
        </Dialog>
      )}
    </Paper>
  );
}

// ============================================================================
// Type Section
// ============================================================================
function TypeSection({ type, elements, onAdd, onEdit, onDelete, onBatchUpload, onReorderType }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.Overig;
  const [open, setOpen] = useState(true);

  if (elements.length === 0 && type !== 'Logo' && type !== 'Kleur') return null;

  // Build full type ordering when a subgroup is reordered. Items not in the subset
  // keep their visual position; subset slots are filled in the new order.
  const handleSubsetReorder = (subsetOriginal, subsetNewOrder) => {
    if (!onReorderType) return;
    const subsetIds = new Set(subsetOriginal.map(e => e.id));
    const queue = [...subsetNewOrder];
    const fullOrder = elements.map(el => subsetIds.has(el.id) ? (queue.shift() || el) : el);
    onReorderType(type, fullOrder.map(e => e.id));
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 2 : 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: config.bg, color: config.color }}>
            {React.cloneElement(config.icon, { sx: { fontSize: 18 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {config.label}
          </Typography>
          <Chip label={elements.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onBatchUpload && (
            <Tooltip title="Upload meerdere bestanden tegelijk">
              <Button
                size="small"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: 'none' }}
              >
                Batch upload
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,.svg,.eps,.ai,.pdf"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) onBatchUpload(files);
                    e.target.value = '';
                  }}
                />
              </Button>
            </Tooltip>
          )}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAdd(type)}
            sx={{ textTransform: 'none' }}
          >
            Toevoegen
          </Button>
        </Box>
      </Box>

      <Collapse in={open}>
      {elements.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center', borderRadius: 1, border: '1px dashed #d1d5db', bgcolor: '#fafafa' }}>
          <Typography variant="body2" color="text.secondary">
            Nog geen {config.label.toLowerCase()} toegevoegd
          </Typography>
        </Box>
      ) : (() => {
        const merken = [...new Set(elements.filter(el => el.fields?.Merk).map(el => el.fields.Merk))].sort((a, b) => a.localeCompare(b, 'nl'));
        const zonderMerk = elements.filter(el => !el.fields?.Merk);

        const renderGrid = (items) => (
          <SortableArea items={items} getId={(it) => it.id}
            onReorderSubset={(newOrder) => handleSubsetReorder(items, newOrder)}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 1.5,
            }}>
              {items.map(el => (
                <SortableItem key={el.id} id={el.id}>
                  <FileCard element={el} onEdit={onEdit} onDelete={onDelete} />
                </SortableItem>
              ))}
            </Box>
          </SortableArea>
        );

        if (merken.length === 0) return renderGrid(elements);

        return (
          <>
            {zonderMerk.length > 0 && <Box sx={{ mb: merken.length > 0 ? 3 : 0 }}>{renderGrid(zonderMerk)}</Box>}
            {merken.map(m => (
              <MerkGroup key={m} merk={m} count={elements.filter(el => el.fields?.Merk === m).length}>
                {renderGrid(elements.filter(el => el.fields?.Merk === m))}
              </MerkGroup>
            ))}
          </>
        );
      })()}
      </Collapse>
    </Box>
  );
}

// ============================================================================
// AE Templates Grouped Section
// ============================================================================
const AE_SUBTYPE_ICONS = {
  Naamtitel: <PersonIcon sx={{ fontSize: 18 }} />,
  Titel: <TitleIcon sx={{ fontSize: 18 }} />,
  Intro: <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />,
  Outro: <StopCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  Narrowcasting: <TvIcon sx={{ fontSize: 18 }} />,
};

function AeTemplatesSection({ elements, fontElements, onAdd, onEdit, onDelete }) {
  const [open, setOpen] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Check which subtypes are covered from configuraties
  const coveredTypes = new Set();
  elements.forEach(el => {
    const cfgs = el.configuraties || [];
    cfgs.forEach(c => { if (c.fields?.Type) coveredTypes.add(c.fields.Type); });
  });
  const missingTypes = AE_SUBTYPES.filter(st => !coveredTypes.has(st));

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 2 : 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#fef3c7', color: '#f59e0b' }}>
            <MovieIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            After Effects Templates
          </Typography>
          <Chip label={elements.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAdd('After Effects')}
            sx={{ textTransform: 'none' }}
          >
            Toevoegen
          </Button>
        </Box>
      </Box>

      <Collapse in={open}>
        {elements.length > 0 ? (() => {
          const renderList = (items) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {items.map(el => {
                const elCfgs = el.configuraties || [];
                const subTypes = [...new Set(elCfgs.map(c => c.fields?.Type).filter(Boolean))];
                const formats = [...new Set(elCfgs.map(c => c.fields?.Formaat).filter(Boolean))];
                const bestand = el.fields?.Bestand;
                const file = Array.isArray(bestand) && bestand.length > 0 ? bestand[0] : null;
                const naam = el.fields?.Naam || file?.filename || 'Naamloos';
                const preview = el.fields?.Preview;
                const previewUrl = Array.isArray(preview) && preview.length > 0
                  ? (preview[0].thumbnails?.large?.url || preview[0].url)
                  : null;

                return (
                  <Paper
                    key={el.id}
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px solid #e5e7eb',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      '&:hover': { borderColor: '#f59e0b', bgcolor: '#fffbeb' },
                      cursor: 'pointer'
                    }}
                    onClick={() => onEdit(el)}
                  >
                    {previewUrl ? (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt={naam}
                        onClick={(e) => { e.stopPropagation(); setLightboxUrl(previewUrl); }}
                        sx={{
                          width: 120,
                          height: 68,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #e5e7eb',
                          flexShrink: 0,
                          cursor: 'zoom-in',
                          '&:hover': { borderColor: '#f59e0b', boxShadow: '0 0 0 1px #f59e0b' },
                        }}
                      />
                    ) : (
                      <Avatar sx={{ width: 40, height: 40, bgcolor: '#fef3c7', color: '#f59e0b' }}>
                        <MovieIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                          {naam}
                        </Typography>
                        {file?.filename && (
                          <Chip
                            label={file.filename.split('.').pop().toUpperCase()}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f3f4f6', color: '#6b7280', letterSpacing: '0.02em', flexShrink: 0 }}
                          />
                        )}
                      </Box>
                      {file && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {file.filename} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                        </Typography>
                      )}
                      {(() => {
                        const linkedFontIds = el.fields?.['Gekoppelde fonts'] || [];
                        if (linkedFontIds.length === 0) return null;
                        const fontMap = {};
                        (fontElements || []).forEach(f => { fontMap[f.id] = f.fields?.Naam || 'Font'; });
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {linkedFontIds.map(fId => (
                              <Chip
                                key={fId}
                                icon={<FontDownloadIcon sx={{ fontSize: 12 }} />}
                                label={fontMap[fId] || 'Font'}
                                size="small"
                                sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                          </Box>
                        );
                      })()}
                      <MetaInfo fields={el.fields} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {(() => {
                        const cfgs = el.configuraties || [];
                        if (cfgs.length > 0) {
                          // Show unique types and formats from configuraties
                          const cfgTypes = [...new Set(cfgs.map(c => c.fields?.Type).filter(Boolean))];
                          const cfgFormats = [...new Set(cfgs.map(c => c.fields?.Formaat).filter(Boolean))];
                          return (
                            <>
                              {cfgTypes.map(st => (
                                <Chip
                                  key={st}
                                  icon={AE_SUBTYPE_ICONS[st] ? React.cloneElement(AE_SUBTYPE_ICONS[st], { sx: { fontSize: 14 } }) : undefined}
                                  label={st}
                                  size="small"
                                  sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500, fontSize: '0.75rem' }}
                                />
                              ))}
                              {cfgFormats.map(f => (
                                <Chip key={f} label={f} size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 500, fontSize: '0.75rem' }} />
                              ))}
                              <Chip label={`${cfgs.length} ${cfgs.length === 1 ? 'configuratie' : 'configuraties'}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, color: '#6b7280' }} />
                            </>
                          );
                        }
                        // Fallback to flat fields
                        return (
                          <>
                            {Array.isArray(subTypes) && subTypes.length > 0 ? subTypes.map(st => (
                              <Chip
                                key={st}
                                icon={AE_SUBTYPE_ICONS[st] ? React.cloneElement(AE_SUBTYPE_ICONS[st], { sx: { fontSize: 14 } }) : undefined}
                                label={st}
                                size="small"
                                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500, fontSize: '0.75rem' }}
                              />
                            )) : (
                              <Chip label="Geen type" size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                            )}
                            {formats.map(f => (
                              <Chip key={f} label={f} size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 500, fontSize: '0.75rem' }} />
                            ))}
                          </>
                        );
                      })()}
                    </Box>
                    {(() => {
                      const bestand = Array.isArray(el?.fields?.Bestand) ? el.fields.Bestand[0] : null;
                      return bestand ? (
                        <Tooltip title={`Download ${bestand.filename || 'bestand'}`}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); downloadElementBestand(el.id, bestand.filename); }}
                            sx={{ flexShrink: 0 }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null;
                    })()}
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onDelete(el); }}
                      sx={{ color: '#ef4444', flexShrink: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                );
              })}
            </Box>
          );

          const merken = [...new Set(elements.filter(el => el.fields?.Merk).map(el => el.fields.Merk))].sort((a, b) => a.localeCompare(b, 'nl'));
          const zonderMerk = elements.filter(el => !el.fields?.Merk);

          if (merken.length === 0) return renderList(elements);

          return (
            <>
              {zonderMerk.length > 0 && <Box sx={{ mb: 2 }}>{renderList(zonderMerk)}</Box>}
              {merken.map(m => (
                <MerkGroup key={m} merk={m} count={elements.filter(el => el.fields?.Merk === m).length}>
                  {renderList(elements.filter(el => el.fields?.Merk === m))}
                </MerkGroup>
              ))}
            </>
          );
        })() : (
          <Box sx={{ p: 3, textAlign: 'center', borderRadius: 1, border: '1px dashed #d1d5db', bgcolor: '#fafafa' }}>
            <Typography variant="body2" color="text.secondary">
              Nog geen After Effects templates toegevoegd
            </Typography>
          </Box>
        )}

        {missingTypes.length > 0 && elements.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Ontbrekend:
            </Typography>
            {missingTypes.map(st => (
              <Chip
                key={st}
                icon={AE_SUBTYPE_ICONS[st] ? React.cloneElement(AE_SUBTYPE_ICONS[st], { sx: { fontSize: 14 } }) : undefined}
                label={st}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', borderStyle: 'dashed' }}
              />
            ))}
          </Box>
        )}
      </Collapse>

      {/* Preview lightbox */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' },
          onClick: () => setLightboxUrl(null),
        }}
      >
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt="Preview"
            sx={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 1,
              cursor: 'pointer',
            }}
          />
        )}
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Narrowcasting Section
// ============================================================================
function NarrowcastingSection({ elements, fontElements, onAdd, onEdit, onDelete }) {
  const [open, setOpen] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 2 : 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#ede9fe', color: '#7c3aed' }}>
            <TvIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Narrowcasting Templates
          </Typography>
          <Chip label={elements.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAdd('Narrowcasting')}
          sx={{ textTransform: 'none' }}
        >
          Toevoegen
        </Button>
      </Box>

      <Collapse in={open}>
        {elements.length > 0 ? (() => {
          const renderList = (items) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {items.map(el => {
                const cfgs = el.configuraties || [];
                const formats = [...new Set(cfgs.map(c => c.fields?.Formaat).filter(Boolean))];
                const bestand = el.fields?.Bestand;
                const file = Array.isArray(bestand) && bestand.length > 0 ? bestand[0] : null;
                const naam = el.fields?.Naam || file?.filename || 'Naamloos';
                const preview = el.fields?.Preview;
                const previewUrl = Array.isArray(preview) && preview.length > 0
                  ? (preview[0].thumbnails?.large?.url || preview[0].url)
                  : null;

                return (
                  <Paper
                    key={el.id}
                    elevation={0}
                    sx={{
                      p: 2, border: '1px solid #e5e7eb', borderRadius: 1,
                      display: 'flex', alignItems: 'center', gap: 2,
                      '&:hover': { borderColor: '#7c3aed', bgcolor: '#faf5ff' },
                      cursor: 'pointer'
                    }}
                    onClick={() => onEdit(el)}
                  >
                    {previewUrl ? (
                      <Box component="img" src={previewUrl} alt={naam}
                        onClick={(e) => { e.stopPropagation(); setLightboxUrl(previewUrl); }}
                        sx={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb', flexShrink: 0, cursor: 'zoom-in', '&:hover': { borderColor: '#7c3aed', boxShadow: '0 0 0 1px #7c3aed' } }}
                      />
                    ) : (
                      <Avatar sx={{ width: 40, height: 40, bgcolor: '#ede9fe', color: '#7c3aed' }}>
                        <TvIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{naam}</Typography>
                        {file?.filename && (
                          <Chip label={file.filename.split('.').pop().toUpperCase()} size="small"
                            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f3f4f6', color: '#6b7280', flexShrink: 0 }} />
                        )}
                      </Box>
                      {file && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {file.filename} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                        </Typography>
                      )}
                      {(() => {
                        const linkedFontIds = el.fields?.['Gekoppelde fonts'] || [];
                        if (linkedFontIds.length === 0) return null;
                        const fontMap = {};
                        (fontElements || []).forEach(f => { fontMap[f.id] = f.fields?.Naam || 'Font'; });
                        return (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {linkedFontIds.map(fId => (
                              <Chip key={fId} icon={<FontDownloadIcon sx={{ fontSize: 12 }} />} label={fontMap[fId] || 'Font'} size="small"
                                sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 500, fontSize: '0.7rem', height: 20 }} />
                            ))}
                          </Box>
                        );
                      })()}
                      <MetaInfo fields={el.fields} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {formats.map(f => (
                        <Chip key={f} label={f} size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 500, fontSize: '0.75rem' }} />
                      ))}
                      <Chip label={`${cfgs.length} slides`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20, color: '#6b7280' }} />
                    </Box>
                    {(() => {
                      const bestand = Array.isArray(el?.fields?.Bestand) ? el.fields.Bestand[0] : null;
                      return bestand ? (
                        <Tooltip title={`Download ${bestand.filename || 'bestand'}`}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); downloadElementBestand(el.id, bestand.filename); }} sx={{ flexShrink: 0 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null;
                    })()}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(el); }} sx={{ color: '#ef4444', flexShrink: 0 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                );
              })}
            </Box>
          );

          const merken = [...new Set(elements.filter(el => el.fields?.Merk).map(el => el.fields.Merk))].sort((a, b) => a.localeCompare(b, 'nl'));
          const zonderMerk = elements.filter(el => !el.fields?.Merk);

          if (merken.length === 0) return renderList(elements);

          return (
            <>
              {zonderMerk.length > 0 && <Box sx={{ mb: 2 }}>{renderList(zonderMerk)}</Box>}
              {merken.map(m => (
                <MerkGroup key={m} merk={m} count={elements.filter(el => el.fields?.Merk === m).length}>
                  {renderList(elements.filter(el => el.fields?.Merk === m))}
                </MerkGroup>
              ))}
            </>
          );
        })() : (
          <Box sx={{ p: 3, textAlign: 'center', borderRadius: 1, border: '1px dashed #d1d5db', bgcolor: '#fafafa' }}>
            <Typography variant="body2" color="text.secondary">
              Nog geen Narrowcasting templates toegevoegd
            </Typography>
          </Box>
        )}
      </Collapse>

      {/* Preview lightbox */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' },
          onClick: () => setLightboxUrl(null),
        }}
      >
        {lightboxUrl && (
          <Box
            component="img"
            src={lightboxUrl}
            alt="Preview"
            sx={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 1,
              cursor: 'pointer',
            }}
          />
        )}
      </Dialog>
    </Box>
  );
}

// ============================================================================
// Kleuren Grouped Section
// ============================================================================
const KLEUR_SUBTYPE_ICONS = {
  Primair: <StarIcon sx={{ fontSize: 18 }} />,
  Secundair: <StarHalfIcon sx={{ fontSize: 18 }} />,
  Accent: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
  Tekst: <TextFieldsIcon sx={{ fontSize: 18 }} />,
  Achtergrond: <WallpaperIcon sx={{ fontSize: 18 }} />,
};

function MerkGroup({ merk, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: open ? 1.5 : 0, ml: 0.5, cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <Chip
          label={merk}
          size="small"
          sx={{ fontWeight: 700, bgcolor: '#fce7f3', color: '#ec4899', fontSize: '0.8rem' }}
        />
        <Chip label={count} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
        {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 18 }} />}
      </Box>
      <Collapse in={open}>
        {children}
      </Collapse>
    </Box>
  );
}

function KleurenSection({ elements, bedrijfNaam, onAdd, onEdit, onDelete, onBatchAdd, onReorderType }) {
  const [open, setOpen] = useState(true);

  const handleSubsetReorder = (subsetOriginal, subsetNewOrder) => {
    if (!onReorderType) return;
    const subsetIds = new Set(subsetOriginal.map(e => e.id));
    const queue = [...subsetNewOrder];
    const fullOrder = elements.map(el => subsetIds.has(el.id) ? (queue.shift() || el) : el);
    onReorderType('Kleur', fullOrder.map(e => e.id));
  };
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchInput, setBatchInput] = useState('');
  const [batchType, setBatchType] = useState('Primair');
  const [batchMerk, setBatchMerk] = useState('');

  // Group by merk, then by type within each merk
  const merken = new Set();
  elements.forEach(el => {
    const merk = el.fields?.['Merk'] || '';
    if (merk) merken.add(merk);
  });
  const merkList = [...merken].sort((a, b) => a.localeCompare(b, 'nl'));

  const groupByType = (items) => {
    const grouped = {};
    KLEUR_SUBTYPES.forEach(st => { grouped[st] = []; });
    grouped['Overig'] = [];
    items.forEach(el => {
      const subType = el.fields?.['Type (Kleur)'] || 'Overig';
      if (!grouped[subType]) grouped[subType] = [];
      grouped[subType].push(el);
    });
    return grouped;
  };

  // Elements without merk
  const zonderMerk = elements.filter(el => !el.fields?.['Merk']);
  // Elements per merk
  const perMerk = {};
  merkList.forEach(m => {
    perMerk[m] = elements.filter(el => el.fields?.['Merk'] === m);
  });

  const handleCopySvgPalette = () => {
    if (elements.length === 0) return;

    const padding = 24;
    const gap = 6;
    const primarySize = 80;
    const smallSize = 50;
    const cornerRadius = 4;
    const headerHeight = 32;
    const sectionGap = 16;
    const groupGap = 24;
    const labelHeight = 18;
    const font = 'Arial, Helvetica, sans-serif';

    // Material Design icon paths (24x24 viewBox)
    const iconPaths = {
      Primair: 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z',
      Secundair: 'M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4V6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z',
      Accent: 'M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z',
      Tekst: 'M2.5 4v3h5v12h3V7h5V4h-13zm19 5h-9v3h3v7h3v-7h3V9z',
      Achtergrond: 'M4 4h7V2H4c-1.1 0-2 .9-2 2v7h2V4zm6 9l-4 5h12l-3-4-2.03 2.71L10 13zm7-4.5c0-.83-.67-1.5-1.5-1.5S14 7.67 14 8.5s.67 1.5 1.5 1.5S17 9.33 17 8.5zM20 2h-7v2h7v7h2V4c0-1.1-.9-2-2-2zm0 18h-7v2h7c1.1 0 2-.9 2-2v-7h-2v7zM4 13H2v7c0 1.1.9 2 2 2h7v-2H4v-7z',
    };

    const categories = [...KLEUR_SUBTYPES, 'Overig'];
    const merkLabelHeight = 22;

    const swatchTextColor = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) || 0;
      const g = parseInt(hex.slice(3, 5), 16) || 0;
      const b = parseInt(hex.slice(5, 7), 16) || 0;
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#000000' : '#FFFFFF';
    };

    const swatchTextOpacity = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) || 0;
      const g = parseInt(hex.slice(3, 5), 16) || 0;
      const b = parseInt(hex.slice(5, 7), 16) || 0;
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '0.5' : '0.7';
    };

    const renderIcon = (pathData, x, y, size, fill) => {
      const scale = size / 24;
      return `<g transform="translate(${x},${y}) scale(${scale})"><path d="${pathData}" fill="${fill}"/></g>`;
    };

    // Build ordered sections: [{merk, items}]
    const sections = [];
    if (merkList.length === 0) {
      sections.push({ merk: null, items: elements });
    } else {
      if (zonderMerk.length > 0) sections.push({ merk: null, items: zonderMerk });
      merkList.forEach(m => sections.push({ merk: m, items: perMerk[m] }));
    }

    // Calculate max row width across all sections
    let maxRowWidth = 0;
    sections.forEach(({ items }) => {
      const byCat = groupByType(items);
      categories.forEach(cat => {
        const catItems = byCat[cat] || [];
        if (catItems.length === 0) return;
        const sz = cat === 'Primair' ? primarySize : smallSize;
        maxRowWidth = Math.max(maxRowWidth, catItems.length * sz + (catItems.length - 1) * gap);
      });
    });
    const svgWidth = Math.max(maxRowWidth + padding * 2, 300);

    const titleColor = '#1f2937';
    const labelColor = '#374151';
    const dimColor = '#6b7280';
    const merkColor = '#ec4899';
    const groupPadding = 12;
    const groupBorderRadius = 8;
    const groupBorderColor = '#e5e7eb';
    const groupBgColor = '#fafafa';

    // Palette icon path (Material Design palette 24x24)
    const paletteIconPath = 'M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10a2.5 2.5 0 002.5-2.5c0-.61-.23-1.21-.64-1.67a.528.528 0 01-.13-.33c0-.28.22-.5.5-.5H16c3.31 0 6-2.69 6-6 0-4.96-4.49-9-10-9zm-5.5 9a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3z';

    // Pre-calculate section heights for group outlines
    const sectionHeights = sections.map(({ merk, items }) => {
      const byCat = groupByType(items);
      let h = merk ? merkLabelHeight + groupPadding : groupPadding;
      categories.forEach(cat => {
        if ((byCat[cat] || []).length === 0) return;
        const sz = cat === 'Primair' ? primarySize : smallSize;
        h += labelHeight + gap + sz + sectionGap;
      });
      // Remove last sectionGap, add bottom padding
      h = h - sectionGap + groupPadding;
      return h;
    });

    // Recalculate total height with group outlines
    let totalHeight = padding + headerHeight + sectionGap;
    sectionHeights.forEach((h, i) => { totalHeight += h + (i < sectionHeights.length - 1 ? groupGap : 0); });
    totalHeight += padding;

    let svg = '';
    let y = padding;

    svg += `<g id="kleurenpalet">`;
    svg += `<rect x="0" y="0" width="${svgWidth}" height="${totalHeight}" rx="10" fill="#FFFFFF"/>`;

    // Header with palette icon
    const now = new Date();
    const dateStr = now.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    svg += `<g id="header">`;
    svg += renderIcon(paletteIconPath, padding, y + 2, 16, merkColor);
    svg += `<text x="${padding + 22}" y="${y + 16}" font-family="${font}" font-size="16" font-weight="bold" fill="${titleColor}">${bedrijfNaam || 'Klant'} Kleurenpalet</text>`;
    svg += `<text x="${padding + 22}" y="${y + 30}" font-family="${font}" font-size="7" fill="${dimColor}">Geëxporteerd uit FF Material App op ${dateStr}</text>`;
    svg += `</g>`;
    y += headerHeight + sectionGap;

    // Render each section (merk group) with outline
    sections.forEach(({ merk, items }, si) => {
      const byCat = groupByType(items);
      const sectionId = merk ? `merk-${merk.toLowerCase().replace(/\s+/g, '-')}` : 'algemeen';
      const groupH = sectionHeights[si];

      svg += `<g id="${sectionId}">`;

      // Group outline
      if (sections.length > 1) {
        svg += `<rect x="${padding - groupPadding}" y="${y - (merk ? 0 : groupPadding)}" width="${svgWidth - padding * 2 + groupPadding * 2}" height="${groupH + (merk ? 0 : groupPadding)}" rx="${groupBorderRadius}" fill="${groupBgColor}" stroke="${groupBorderColor}" stroke-width="1"/>`;
      }

      // Merk label (pill on top edge of outline)
      if (merk) {
        const textWidth = merk.length * 6.5 + 20;
        const pillX = padding;
        const pillY = y - 8;
        svg += `<rect x="${pillX}" y="${pillY}" width="${textWidth}" height="16" rx="8" fill="${merkColor}"/>`;
        svg += `<text x="${pillX + textWidth / 2}" y="${pillY + 11.5}" text-anchor="middle" font-family="${font}" font-size="8" font-weight="700" fill="#FFFFFF">${merk}</text>`;
        y += merkLabelHeight;
      } else if (sections.length > 1) {
        y += groupPadding;
      }

      // Categories within section
      categories.forEach(cat => {
        const catItems = byCat[cat] || [];
        if (catItems.length === 0) return;
        const isPrimary = cat === 'Primair';
        const sz = isPrimary ? primarySize : smallSize;
        const fontSize = isPrimary ? 8 : 6;
        const iconSize = 12;

        svg += `<g id="${sectionId}-${cat.toLowerCase()}">`;

        if (iconPaths[cat]) {
          svg += renderIcon(iconPaths[cat], padding, y, iconSize, labelColor);
        }
        const textX = iconPaths[cat] ? padding + iconSize + 4 : padding;
        svg += `<text x="${textX}" y="${y + 10}" font-family="${font}" font-size="10" font-weight="600" fill="${labelColor}">${cat}</text>`;
        y += labelHeight + gap;

        catItems.forEach((el, i) => {
          const hex = (el.fields?.Kleurcode || '#000000').toUpperCase();
          const naam = el.fields?.Naam || '';
          const x = padding + i * (sz + gap);
          const tc = swatchTextColor(hex);
          const to = swatchTextOpacity(hex);

          svg += `<g>`;
          svg += `<rect x="${x}" y="${y}" width="${sz}" height="${sz}" rx="${cornerRadius}" fill="${hex}"/>`;
          svg += `<text x="${x + sz / 2}" y="${y + sz / 2 - 2}" text-anchor="middle" font-family="${font}" font-size="${fontSize}" font-weight="bold" fill="${tc}">${naam}</text>`;
          svg += `<text x="${x + sz / 2}" y="${y + sz / 2 + fontSize + 2}" text-anchor="middle" font-family="${font}" font-size="${fontSize}" fill="${tc}" opacity="${to}">${hex}</text>`;
          svg += `</g>`;
        });
        y += sz + sectionGap;

        svg += `</g>`;
      });

      // Adjust y: undo last sectionGap inside group, add groupPadding + gap between groups
      y = y - sectionGap + groupPadding;
      if (si < sections.length - 1) y += groupGap;

      svg += `</g>`;
    });

    svg += `</g>`;

    const fullSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${totalHeight}" viewBox="0 0 ${svgWidth} ${totalHeight}">${svg}</svg>`;
    navigator.clipboard.writeText(fullSvg);
    toast.success(`${elements.length} kleuren als SVG gekopieerd — plak in Illustrator`);
  };

  const parsedColors = batchInput
    .split(/[\n,;\s]+/)
    .map(s => s.trim().toUpperCase())
    .filter(s => /^#?[0-9A-F]{3,8}$/.test(s))
    .map(s => s.startsWith('#') ? s : `#${s}`);

  const config = TYPE_CONFIG.Kleur;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 2 : 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: config.bg, color: config.color }}>
            {React.cloneElement(config.icon, { sx: { fontSize: 18 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {config.label}
          </Typography>
          <Chip label={elements.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {elements.length > 0 && (
            <Tooltip title="Kopieer alle kleuren als SVG om te plakken in Adobe Illustrator">
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopySvgPalette}
                sx={{ textTransform: 'none' }}
              >
                Kopieer SVG
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Voeg meerdere kleuren tegelijk toe met kleurcodes">
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setBatchOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Batch
            </Button>
          </Tooltip>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAdd('Kleur')}
            sx={{ textTransform: 'none' }}
          >
            Toevoegen
          </Button>
        </Box>
      </Box>

      {/* Batch add dialog */}
      <Dialog open={batchOpen} onClose={() => setBatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kleuren batch toevoegen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Categorie
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {KLEUR_SUBTYPES.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => setBatchType(t)}
                    variant={batchType === t ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: batchType === t ? 600 : 400,
                      cursor: 'pointer',
                      ...(batchType === t && { bgcolor: '#fce7f3', color: '#ec4899' }),
                    }}
                  />
                ))}
              </Box>
            </Box>
            <TextField
              label="Merk (optioneel)"
              value={batchMerk}
              onChange={(e) => setBatchMerk(e.target.value)}
              size="small"
              placeholder="Bijv. Enexis, Vattenfall"
            />
            <TextField
              label="Kleurcodes"
              value={batchInput}
              onChange={(e) => setBatchInput(e.target.value)}
              multiline
              rows={4}
              placeholder={"#FF5500\n#2196F3\n#4CAF50"}
              helperText="Eén kleurcode per regel, of gescheiden door komma's of spaties"
              size="small"
            />
            {parsedColors.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Voorbeeld ({parsedColors.length} kleur{parsedColors.length !== 1 ? 'en' : ''})
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {parsedColors.map((c, i) => (
                    <Chip
                      key={i}
                      label={c}
                      size="small"
                      sx={{
                        bgcolor: c,
                        color: parseInt(c.replace('#', '').substring(0, 6), 16) > 0x888888 ? '#000' : '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                  Namen: {batchType} 1, {batchType} 2, ...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchOpen(false)}>Annuleren</Button>
          <Button
            variant="contained"
            disabled={parsedColors.length === 0}
            onClick={() => {
              setBatchOpen(false);
              onBatchAdd(parsedColors, batchType, batchMerk);
              setBatchInput('');
              setBatchMerk('');
            }}
          >
            {parsedColors.length} kleur{parsedColors.length !== 1 ? 'en' : ''} toevoegen
          </Button>
        </DialogActions>
      </Dialog>

      <Collapse in={open}>
      {(() => {
        const renderTypeGroups = (items, keyPrefix) => {
          const grouped = groupByType(items);
          return [...KLEUR_SUBTYPES, 'Overig'].map(subType => {
            const subElements = grouped[subType] || [];
            if (subElements.length === 0) return null;
            return (
              <Box key={`${keyPrefix}-${subType}`} sx={{ mb: 2, ml: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {KLEUR_SUBTYPE_ICONS[subType] && React.cloneElement(KLEUR_SUBTYPE_ICONS[subType], { sx: { fontSize: 18, color: config.color } })}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b5563' }}>
                      {subType}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => onAdd('Kleur', subType)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Toevoegen
                  </Button>
                </Box>
                <SortableArea items={subElements} getId={(it) => it.id}
                  onReorderSubset={(newOrder) => handleSubsetReorder(subElements, newOrder)}>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                    gap: 1.5,
                  }}>
                    {subElements.map(el => (
                      <SortableItem key={el.id} id={el.id}>
                        <ColorSwatch element={el} onEdit={onEdit} onDelete={onDelete} />
                      </SortableItem>
                    ))}
                  </Box>
                </SortableArea>
              </Box>
            );
          });
        };

        if (merkList.length === 0) {
          // No merken at all, render flat
          return renderTypeGroups(elements, 'all');
        }

        return (
          <>
            {zonderMerk.length > 0 && renderTypeGroups(zonderMerk, 'geen-merk')}
            {merkList.map(merk => (
              <MerkGroup key={merk} merk={merk} count={perMerk[merk].length}>
                {renderTypeGroups(perMerk[merk], merk)}
              </MerkGroup>
            ))}
          </>
        );
      })()}

      {elements.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center', borderRadius: 1, border: '1px dashed #d1d5db', bgcolor: '#fafafa' }}>
          <Typography variant="body2" color="text.secondary">
            Nog geen kleuren toegevoegd
          </Typography>
        </Box>
      )}
      </Collapse>
    </Box>
  );
}

// ============================================================================
// Fonts Grouped Section
// ============================================================================
const FONT_SUBTYPE_ICONS = {
  Heading: <TitleIcon sx={{ fontSize: 18 }} />,
  Body: <SubjectIcon sx={{ fontSize: 18 }} />,
  Accent: <FormatColorTextIcon sx={{ fontSize: 18 }} />,
};

function FontsSection({ elements, bedrijfNaam, onAdd, onEdit, onDelete, aeTemplates, onBatchUpload, onReorderType }) {
  const [open, setOpen] = useState(true);

  const handleSubsetReorder = (subsetOriginal, subsetNewOrder) => {
    if (!onReorderType) return;
    const subsetIds = new Set(subsetOriginal.map(e => e.id));
    const queue = [...subsetNewOrder];
    const fullOrder = elements.map(el => subsetIds.has(el.id) ? (queue.shift() || el) : el);
    onReorderType('Font', fullOrder.map(e => e.id));
  };

  const handleBatchDownload = async () => {
    const fontsWithFiles = elements.filter(el => {
      const bestand = el.fields?.Bestand;
      return Array.isArray(bestand) && bestand.length > 0;
    });
    if (fontsWithFiles.length === 0) {
      toast.error('Geen fonts met bestanden gevonden');
      return;
    }
    const toastId = toast.loading(`0/${fontsWithFiles.length} fonts downloaden...`);
    const zip = new JSZip();
    let success = 0;
    for (let i = 0; i < fontsWithFiles.length; i++) {
      const el = fontsWithFiles[i];
      const file = el.fields.Bestand[0];
      const filename = file.filename || `${el.fields?.Naam || 'font'}.ttf`;
      toast.loading(`${i + 1}/${fontsWithFiles.length} — ${filename}`, { id: toastId });
      try {
        const response = await fetch(file.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        zip.file(filename, blob);
        success++;
      } catch (err) {
        console.error(`Download failed for ${filename}:`, err);
      }
    }
    toast.dismiss(toastId);
    if (success === 0) {
      toast.error('Geen fonts konden worden gedownload');
      return;
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const zipName = `${bedrijfNaam || 'Fonts'} - Fonts.zip`;
    saveAs(zipBlob, zipName);
    toast.success(`${success} font${success > 1 ? 's' : ''} gedownload als ZIP`);
  };

  const config = TYPE_CONFIG.Font;

  const groupByType = (items) => {
    const grouped = {};
    FONT_SUBTYPES.forEach(st => { grouped[st] = []; });
    grouped['Overig'] = [];
    items.forEach(el => {
      const subType = el.fields?.['Type (Font)'] || 'Overig';
      if (!grouped[subType]) grouped[subType] = [];
      grouped[subType].push(el);
    });
    return grouped;
  };

  const renderSubTypeGroups = (items) => (
    <>
      {[...FONT_SUBTYPES, 'Overig'].map(subType => {
        const grouped = groupByType(items);
        const subElements = grouped[subType] || [];
        if (subElements.length === 0) return null;
        return (
          <Box key={subType} sx={{ mb: 2, ml: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {FONT_SUBTYPE_ICONS[subType] && React.cloneElement(FONT_SUBTYPE_ICONS[subType], { sx: { fontSize: 18, color: config.color } })}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4b5563' }}>
                  {subType}
                </Typography>
              </Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAdd('Font', subType)}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Toevoegen
              </Button>
            </Box>
            <SortableArea items={subElements} getId={(it) => it.id}
              onReorderSubset={(newOrder) => handleSubsetReorder(subElements, newOrder)}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 1.5,
              }}>
                {subElements.map(el => (
                  <SortableItem key={el.id} id={el.id}>
                    <FileCard element={el} onEdit={onEdit} onDelete={onDelete} aeTemplates={aeTemplates} />
                  </SortableItem>
                ))}
              </Box>
            </SortableArea>
          </Box>
        );
      })}
    </>
  );

  const merken = [...new Set(elements.filter(el => el.fields?.Merk).map(el => el.fields.Merk))].sort((a, b) => a.localeCompare(b, 'nl'));
  const zonderMerk = elements.filter(el => !el.fields?.Merk);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open ? 2 : 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setOpen(o => !o)}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: config.bg, color: config.color }}>
            {React.cloneElement(config.icon, { sx: { fontSize: 18 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {config.label}
          </Typography>
          <Chip label={elements.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          {open ? <ExpandLessIcon sx={{ color: '#9ca3af', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af', fontSize: 20 }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {elements.some(el => Array.isArray(el.fields?.Bestand) && el.fields.Bestand.length > 0) && (
            <Tooltip title="Download alle fonts als ZIP-bestand">
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleBatchDownload}
                sx={{ textTransform: 'none' }}
              >
                Download alle
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Upload meerdere fontbestanden tegelijk (.ttf, .otf, .woff, .woff2)">
            <Button
              size="small"
              component="label"
              startIcon={<CloudUploadIcon />}
              sx={{ textTransform: 'none' }}
            >
              Batch upload
              <input
                type="file"
                hidden
                multiple
                accept=".ttf,.otf,.woff,.woff2"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) onBatchUpload(files);
                  e.target.value = '';
                }}
              />
            </Button>
          </Tooltip>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAdd('Font')}
            sx={{ textTransform: 'none' }}
          >
            Toevoegen
          </Button>
        </Box>
      </Box>

      <Collapse in={open}>
      {elements.length > 0 ? (
        merken.length === 0 ? renderSubTypeGroups(elements) : (
          <>
            {zonderMerk.length > 0 && <Box sx={{ mb: merken.length > 0 ? 3 : 0 }}>{renderSubTypeGroups(zonderMerk)}</Box>}
            {merken.map(m => (
              <MerkGroup key={m} merk={m} count={elements.filter(el => el.fields?.Merk === m).length}>
                {renderSubTypeGroups(elements.filter(el => el.fields?.Merk === m))}
              </MerkGroup>
            ))}
          </>
        )
      ) : (
        <Box sx={{ p: 3, textAlign: 'center', borderRadius: 1, border: '1px dashed #d1d5db', bgcolor: '#fafafa' }}>
          <Typography variant="body2" color="text.secondary">
            Nog geen fonts toegevoegd
          </Typography>
        </Box>
      )}
      </Collapse>
    </Box>
  );
}

// ============================================================================
// BrandPortalBar — top bar for the embedded Huisstijl tab.
// Stores the URL on the account_profile (PostgreSQL) so it survives Airtable churn.
// ============================================================================
function BrandPortalBar({ accountId }) {
  const [value, setValue] = React.useState('');
  const [saved, setSaved] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/profile`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        const url = data?.profile?.brand_portal_url || '';
        if (!cancelled) { setValue(url); setSaved(url); }
      } catch {
        if (!cancelled) { setValue(''); setSaved(''); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  const normalize = (v) => {
    const t = (v || '').trim();
    if (!t) return '';
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };

  const persist = async (next) => {
    if (next === saved) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_airtable_id: accountId, brand_portal_url: next || null }),
      });
      if (!r.ok) throw new Error();
      setSaved(next);
      toast.success(next ? 'Brandportaal-URL opgeslagen' : 'Brandportaal-URL leeggemaakt');
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const open = () => {
    const url = normalize(value);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) return null;

  const hasValue = !!normalize(value);

  return (
    <Box sx={{
      mb: 3, p: 2, borderRadius: '0.625rem',
      bgcolor: T.surface, border: `1px solid ${hasValue ? 'rgba(99,89,131,0.18)' : 'rgba(45,51,53,0.06)'}`,
      display: 'flex', alignItems: 'center', gap: 1.5,
      transition: 'border-color 0.15s',
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '0.5rem', flexShrink: 0,
        bgcolor: hasValue ? T.tertiaryContainer : T.surfaceLow,
        color: hasValue ? T.tertiary : T.outline,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <StitchIcon name="hub" size={20} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{
          fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outline,
          lineHeight: 1, mb: 0.5,
        }}>
          Brandportaal
        </Box>
        <TextField
          fullWidth size="small" variant="standard"
          placeholder="https://brandportal.example.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => persist(normalize(value))}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontFamily: T.fontBody, fontSize: '0.86rem', color: T.text,
              '& input::placeholder': { color: T.outlineVariant, opacity: 1 },
            },
          }}
          disabled={busy}
        />
      </Box>
      {hasValue && (
        <Button
          size="small" variant="outlined"
          onClick={open}
          startIcon={<StitchIcon name="open_in_new" size={14} />}
          sx={{
            textTransform: 'none', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem',
            borderRadius: '0.4rem', borderColor: 'rgba(99,89,131,0.3)', color: T.tertiary,
            '&:hover': { borderColor: T.tertiary, bgcolor: 'rgba(99,89,131,0.06)' },
          }}
        >
          Openen
        </Button>
      )}
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================
export default function KlantDetail({ embedded = false, bedrijfId: propBedrijfId }) {
  const params = useParams();
  const bedrijfId = propBedrijfId || params.bedrijfId;
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';

  const [loading, setLoading] = useState(true);
  const [bedrijf, setBedrijf] = useState(null);
  const [huisstijl, setHuisstijl] = useState([]);
  const [error, setError] = useState(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [editElement, setEditElement] = useState(null);
  const [defaultType, setDefaultType] = useState('Logo');
  const [defaultTypeKleur, setDefaultTypeKleur] = useState('');
  const [defaultTypeFont, setDefaultTypeFont] = useState('');
  const [defaultBronFont, setDefaultBronFont] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountRes, huisstijlRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}`).then(r =>
          r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))
        ),
        authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl`).then(r =>
          r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))
        ),
      ]);

      setBedrijf(accountRes.account || null);
      const newHuisstijl = huisstijlRes.huisstijl || [];
      setHuisstijl(newHuisstijl);
      // Update editElement if dialog is open so preview etc. refreshes
      setEditElement(prev => {
        if (!prev) return prev;
        const updated = newHuisstijl.find(el => el.id === prev.id);
        return updated || prev;
      });
    } catch (err) {
      setError(`Kon gegevens niet laden: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [bedrijfId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = (type, subType) => {
    setEditElement(null);
    setDefaultType(type);
    setDefaultTypeKleur(type === 'Kleur' ? (subType || '') : '');
    setDefaultTypeFont(type === 'Font' ? (subType || '') : '');
    setDefaultBronFont('');
    setDialogOpen(true);
  };

  const handleReorderType = async (type, orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;
    // Optimistic update: reorder type's items in-place within the global array
    setHuisstijl(prev => {
      const idsInOrder = orderedIds;
      const typeItemsById = {};
      prev.forEach(el => {
        if (el.fields?.Type === type) typeItemsById[el.id] = el;
      });
      const newOrderedTypeItems = idsInOrder.map(id => typeItemsById[id]).filter(Boolean);
      let queueIdx = 0;
      return prev.map(el =>
        el.fields?.Type === type ? (newOrderedTypeItems[queueIdx++] || el) : el
      );
    });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, ordered_ids: orderedIds }),
      });
      if (!res.ok) throw new Error();
    } catch {
      toast.error('Volgorde niet opgeslagen');
      loadData();  // re-sync from server
    }
  };

  const handleEdit = (element) => {
    setEditElement(element);
    setDefaultType(element.fields?.Type || 'Logo');
    setDefaultTypeKleur(element.fields?.['Type (Kleur)'] || '');
    setDefaultTypeFont(element.fields?.['Type (Font)'] || '');
    setDefaultBronFont(element.fields?.['Bron (Font)'] || '');
    setDialogOpen(true);
  };

  const handleDelete = async (element) => {
    if (!(await bevestig({ titel: `"${element.fields?.Naam || 'Element'}" verwijderen?`, tekst: 'Het huisstijl-element wordt uit Airtable verwijderd. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${element.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Element verwijderd');
      setHuisstijl(prev => prev.filter(h => h.id !== element.id));
    } catch (err) {
      toast.error(`Fout bij verwijderen: ${err.message}`);
    }
  };

  const handleSave = async (data, file) => {
    const payload = { ...data, userEmail };
    let recordId;

    if (editElement) {
      // Update existing
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${editElement.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      recordId = editElement.id;
      toast.success('Element bijgewerkt');
    } else {
      // Create new
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      recordId = result.record?.id;
      toast.success('Element toegevoegd');
    }

    // Upload file separately (non-fatal: record is already saved)
    if (file && recordId) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${recordId}/upload`, {
          method: 'POST',
          body: formData,
        });
        if (!uploadRes.ok) {
          console.error('File upload failed:', uploadRes.status);
          toast.error('Bestand upload mislukt — het element is wel opgeslagen');
        }
      } catch (uploadErr) {
        console.error('File upload error:', uploadErr);
        toast.error('Bestand upload mislukt — het element is wel opgeslagen');
      }
    }

    // Reload data
    loadData();
  };

  const handleBatchUploadLogos = async (files) => {
    const toastId = toast.loading(`0/${files.length} logo's uploaden...`);
    let success = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const naam = file.name.replace(/\.[^/.]+$/, '');
      toast.loading(`${i + 1}/${files.length} — ${naam}`, { id: toastId });
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ naam, type: 'Logo', userEmail }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        const recordId = result.record?.id;
        if (recordId) {
          const formData = new FormData();
          formData.append('file', file);
          await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${recordId}/upload`, {
            method: 'POST',
            body: formData,
          });
        }
        success++;
      } catch (err) {
        console.error(`Batch logo upload failed for ${naam}:`, err);
      }
    }
    toast.dismiss(toastId);
    if (success > 0) {
      toast.success(`${success} logo${success > 1 ? "'s" : ''} toegevoegd`);
      loadData();
    }
    if (success < files.length) {
      toast.error(`${files.length - success} logo('s) mislukt`);
    }
  };

  const handleBatchUploadFonts = async (files) => {
    const toastId = toast.loading(`0/${files.length} fonts uploaden...`);
    let success = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const naam = file.name.replace(/\.[^/.]+$/, '');
      toast.loading(`${i + 1}/${files.length} — ${naam}`, { id: toastId });
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ naam, type: 'Font', bronFont: 'Bestand', userEmail }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        const recordId = result.record?.id;
        if (recordId) {
          const formData = new FormData();
          formData.append('file', file);
          await authFetch(`${BACKEND_URL}/Api/klanten/huisstijl/${recordId}/upload`, {
            method: 'POST',
            body: formData,
          });
        }
        success++;
      } catch (err) {
        console.error(`Batch font upload failed for ${naam}:`, err);
      }
    }
    toast.dismiss(toastId);
    if (success > 0) {
      toast.success(`${success} font${success > 1 ? 's' : ''} toegevoegd`);
      loadData();
    }
    if (success < files.length) {
      toast.error(`${files.length - success} font(s) mislukt`);
    }
  };

  const handleBatchAddColors = async (colors, typeKleur, merk) => {
    // colors = array of hex strings, typeKleur = category name, merk = optional brand
    const toastId = toast.loading(`0/${colors.length} kleuren toevoegen...`);
    let success = 0;
    // Count existing colors in this category to determine starting number
    const existing = huisstijl.filter(el =>
      el.fields?.Type === 'Kleur' && el.fields?.['Type (Kleur)'] === typeKleur
    ).length;
    for (let i = 0; i < colors.length; i++) {
      const naam = `${typeKleur} ${existing + success + 1}`;
      toast.loading(`${i + 1}/${colors.length} — ${naam}`, { id: toastId });
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/${bedrijfId}/huisstijl`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ naam, type: 'Kleur', typeKleur, merk: merk || undefined, kleurcode: colors[i], userEmail }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        success++;
      } catch (err) {
        console.error(`Batch color add failed for ${colors[i]}:`, err);
      }
    }
    toast.dismiss(toastId);
    if (success > 0) {
      toast.success(`${success} kleur${success > 1 ? 'en' : ''} toegevoegd`);
      loadData();
    }
    if (success < colors.length) {
      toast.error(`${colors.length - success} kleur(en) mislukt`);
    }
  };

  // Group huisstijl by type
  const groupedByType = {};
  TYPES.forEach(t => { groupedByType[t] = []; });
  huisstijl.forEach(el => {
    const type = el.fields?.Type || 'Overig';
    if (!groupedByType[type]) groupedByType[type] = [];
    groupedByType[type].push(el);
  });

  const bedrijfNaam = bedrijf?.fields?.Account || 'Klant';
  const beeldmerk = bedrijf?.fields?.Beeldmerk;
  const logoUrl = Array.isArray(beeldmerk) && beeldmerk.length > 0
    ? (beeldmerk[0]?.thumbnails?.large?.url || beeldmerk[0]?.url)
    : null;

  return (
    <Box sx={{ width: '100%', ...(embedded ? {} : { maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }) }}>
      {/* Back button — hidden when embedded */}
      {!embedded && (
        <Box
          onClick={() => navigate('/accounts')}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, mb: 3,
            px: 2.5, py: 1, borderRadius: '0.375rem', bgcolor: T.surface, cursor: 'pointer',
            fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem', color: T.primary,
            boxShadow: T.shadow, transition: 'all 0.15s',
            '&:hover': { boxShadow: '0 4px 12px rgba(45,51,53,0.08)' },
          }}
        >
          <StitchIcon name="arrow_back" size={16} sx={{ color: T.primary }} />
          Alle accounts
        </Box>
      )}

      {/* Header — full when standalone, compact when embedded */}
      {!embedded && (
        loading ? (
          <Skeleton variant="rounded" width={300} height={48} sx={{ mb: 4, borderRadius: '0.375rem' }} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt={bedrijfNaam}
                sx={{ width: 48, height: 48, borderRadius: '0.375rem', objectFit: 'contain', bgcolor: T.surfaceLow, p: 0.5, flexShrink: 0 }}
              />
            ) : (
              <Box sx={{
                width: 48, height: 48, borderRadius: '0.375rem',
                bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: T.primary }}>
                  {bedrijfNaam.substring(0, 2).toUpperCase()}
                </Typography>
              </Box>
            )}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: T.font, fontSize: '1.8rem', fontWeight: 800, color: T.text, letterSpacing: '-0.03em' }}>
                  {bedrijfNaam}
                </Typography>
                {bedrijf?.fields?.Sector && (() => {
                  const sc = SECTOR_COLORS[bedrijf.fields.Sector] || { color: T.outline, bg: T.surfaceLow };
                  return (
                    <Typography sx={{
                      fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700,
                      color: sc.color, bgcolor: sc.bg,
                      px: 1.25, py: 0.3, borderRadius: '9999px', display: 'inline-block',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      {bedrijf.fields.Sector}
                    </Typography>
                  );
                })()}
              </Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>
                Huisstijl &mdash; {huisstijl.length} {huisstijl.length === 1 ? 'element' : 'elementen'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none', fontFamily: T.fontBody, fontWeight: 600,
                bgcolor: '#2d3335', color: '#fff', borderRadius: '0.375rem',
                '&:hover': { bgcolor: '#404849' },
              }}
            >
              Toevoegen
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              PaperProps={{ sx: { borderRadius: '0.5rem', mt: 0.5, minWidth: 200 } }}
            >
              {TYPES.map(type => {
                const config = TYPE_CONFIG[type] || TYPE_CONFIG.Overig;
                return (
                  <MuiMenuItem
                    key={type}
                    onClick={() => { setAddMenuAnchor(null); handleAdd(type); }}
                    sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {React.cloneElement(config.icon, { sx: { fontSize: 20, color: config.color } })}
                    </ListItemIcon>
                    {config.label}
                  </MuiMenuItem>
                );
              })}
            </Menu>
          </Box>
          </Box>
        )
      )}
      {embedded && !loading && (
        <BrandPortalBar accountId={bedrijfId} />
      )}
      {embedded && !loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>
            {huisstijl.length} {huisstijl.length === 1 ? 'element' : 'elementen'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.82rem',
              bgcolor: '#2d3335', color: '#fff', borderRadius: '0.375rem',
              '&:hover': { bgcolor: '#404849' },
            }}
          >
            Toevoegen
          </Button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={() => setAddMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: '0.5rem', mt: 0.5, minWidth: 200 } }}
          >
            {TYPES.map(type => {
              const config = TYPE_CONFIG[type] || TYPE_CONFIG.Overig;
              return (
                <MuiMenuItem
                  key={type}
                  onClick={() => { setAddMenuAnchor(null); handleAdd(type); }}
                  sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {React.cloneElement(config.icon, { sx: { fontSize: 20, color: config.color } })}
                  </ListItemIcon>
                  {config.label}
                </MuiMenuItem>
              );
            })}
          </Menu>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box sx={{ p: 2.5, mb: 4, borderRadius: '0.375rem', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.84rem', color: '#991b1b' }}>{error}</Typography>
        </Box>
      )}

      {/* Account info — only shown standalone, not embedded */}
      {!embedded && !loading && bedrijf && (
        <AccountInfo fields={bedrijf.fields} />
      )}

      {/* Huisstijl sections */}
      {!loading && (
        <>
          <Box sx={{ height: '1px', bgcolor: 'rgba(45,51,53,0.06)', mb: 3 }} />
          {/* Regular types (non-AE, non-Narrowcasting, non-Kleur, non-Font) */}
          {TYPES.filter(t => t !== 'After Effects' && t !== 'Narrowcasting' && t !== 'Kleur' && t !== 'Font').map(type => (
            <TypeSection
              key={type}
              type={type}
              elements={groupedByType[type]}
              onAdd={handleAdd}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onBatchUpload={type === 'Logo' ? handleBatchUploadLogos : undefined}
              onReorderType={handleReorderType}
            />
          ))}
          {/* Kleuren grouped section */}
          <KleurenSection
            elements={groupedByType['Kleur'] || []}
            bedrijfNaam={bedrijfNaam}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBatchAdd={handleBatchAddColors}
            onReorderType={handleReorderType}
          />
          {/* Fonts grouped section */}
          <FontsSection
            elements={groupedByType['Font'] || []}
            bedrijfNaam={bedrijfNaam}
            aeTemplates={groupedByType['After Effects'] || []}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBatchUpload={handleBatchUploadFonts}
            onReorderType={handleReorderType}
          />
          {/* AE Templates grouped section */}
          <AeTemplatesSection
            elements={groupedByType['After Effects'] || []}
            fontElements={groupedByType['Font'] || []}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
          {/* Narrowcasting templates section */}
          <NarrowcastingSection
            elements={groupedByType['Narrowcasting'] || []}
            fontElements={groupedByType['Font'] || []}
            onAdd={handleAdd}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </>
      )}

      {/* Loading skeletons */}
      {loading && (
        <Box sx={{ mt: 3 }}>
          {[1, 2, 3].map(i => (
            <Box key={i} sx={{ mb: 3 }}>
              <Skeleton variant="text" width={150} height={30} sx={{ mb: 1, borderRadius: '0.375rem' }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '0.375rem' }} />
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '0.375rem' }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <HuisstijlDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
        element={editElement}
        defaultType={defaultType}
        defaultTypeKleur={defaultTypeKleur}
        defaultTypeFont={defaultTypeFont}
        defaultBronFont={defaultBronFont}
        clientFonts={groupedByType['Font'] || []}
        onRefresh={loadData}
      />
    </Box>
  );
}
